import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { AuthContext } from '../../components/AuthContext';
import Select from 'react-select';
import { ClockIcon } from '@heroicons/react/24/outline';
import ProgressModal from '../../components/ProgressModal';
import UpdatedByModal from '../../components/UpdatedByModal';
import useDraftManager from '../../hooks/useDraftManager';
import { toast } from 'react-toastify';
import QRCode from 'qrcode';
import QRCodeSVG from 'react-qr-code';
import QRScanner from '../../components/QRScanner/QRScanner';
import QrScanner from 'qr-scanner';
import { jsPDF } from 'jspdf';
import { FaDownload, FaTimes, FaFilePdf, FaQrcode, FaCamera } from 'react-icons/fa';

const ItemManagement = () => {
  const [brands, setBrands] = useState([]);
  const [items, setItems] = useState([]);
  const { user } = useContext(AuthContext);
  
  // Dark mode state
  const [darkMode, setDarkMode] = useState(() => {
    const savedMode = localStorage.getItem('darkMode');
    return savedMode === 'true';
  });

  // Enhanced dark mode sync with localStorage and system preference
  useEffect(() => {
    const updateDarkMode = () => {
      const savedMode = localStorage.getItem('darkMode');
      const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      const isDark = savedMode === 'true' || (savedMode === null && systemPrefersDark);
      setDarkMode(isDark);
    };

    // Initial check
    updateDarkMode();

    // Listen for localStorage changes
    const interval = setInterval(updateDarkMode, 500);

    // Listen for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addEventListener('change', updateDarkMode);

    return () => {
      clearInterval(interval);
      mediaQuery.removeEventListener('change', updateDarkMode);
    };
  }, []);
  
  const [form, setForm] = useState({
    brand: '',
    category: '',
    skuCode: '',
    skuDescription: '',
    series: 'RS',
    hsnCode: '',
    gstRate: '',
    mrp: '',
    offerType: {},
    image: null,
    additionalFieldValues: {},
    subItems: [],
    variants: [],
    isActive: true
  });
  const [isEditing, setIsEditing] = useState(false);
  const [filteredItems, setFilteredItems] = useState(items);
  // List vs Tree (Item -> Variants -> Sub-items) view
  const [itemViewMode, setItemViewMode] = useState('list');
  const [expandedTreeItems, setExpandedTreeItems] = useState({});
  const toggleTreeItem = (id) => setExpandedTreeItems(prev => ({ ...prev, [id]: !prev[id] }));
  const [filter, setFilter] = useState('');
  // Brand filter for the item list (distinct from the create/edit form brand)
  const [listBrand, setListBrand] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [editingOtherImageIds, setEditingOtherImageIds] = useState([]);
  const [otherImageFiles, setOtherImageFiles] = useState([]);
  const [otherImageRemoved, setOtherImageRemoved] = useState([]);
  const [primaryOtherImageId, setPrimaryOtherImageId] = useState(null);
  const [viewImageIndex, setViewImageIndex] = useState(0);

  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(5);

  // Modal state for showing details
  const [modalOpen, setModalOpen] = useState(false);
  const [modalItem, setModalItem] = useState(null);
  const [similarItems, setSimilarItems] = useState([]);
  const [expandedUpdates, setExpandedUpdates] = useState({});

  const [loading, setLoading] = useState(true);
  const [isWebImageSearching, setIsWebImageSearching] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [brandAdditionalFields, setBrandAdditionalFields] = useState([]);

  // Additional fields visible for the currently selected category and context.
  const fieldInCategory = (f) => !(f.categories && f.categories.length) ||
    (form.category && f.categories.map(String).includes(String(form.category)));
  const visibleAdditionalFields = brandAdditionalFields.filter(
    (f) => !f.derived && (f.scope || 'both') !== 'accessory' && fieldInCategory(f)
  );
  const visibleVariantFields = brandAdditionalFields.filter(
    (f) => (f.scope || 'both') === 'both' && fieldInCategory(f)
  );
  const visibleAccessoryFields = brandAdditionalFields.filter(
    (f) => (f.scope || 'both') === 'accessory' && fieldInCategory(f)
  );
  const isAccessoryContext =
    String(form.additionalFieldValues?.['Product Type'] || '').trim().toLowerCase() === 'accessory';

  const apiBase = process.env.REACT_APP_API_BASE_URL;
  const viewImages = modalItem ? [
    { url: `${apiBase}/items/image/${modalItem._id}` },
    ...((modalItem.otherImageIds || []).map(id => ({ url: `${apiBase}/items/image-by-id/${id}` })))
  ] : [];
  const keptOtherImageIds = editingOtherImageIds.filter(id => !otherImageRemoved.map(String).includes(String(id)));
  const otherImageItems = [
    ...keptOtherImageIds.map(id => ({ id: String(id), url: `${apiBase}/items/image-by-id/${id}`, existing: true })),
    ...otherImageFiles.map(file => ({ file, url: URL.createObjectURL(file), existing: false }))
  ];

  // Options for the per-variant "preferred sub-items" multi-select.
  const subItemOptionList = (form.subItems || []).map(s => ({
    value: typeof s.item === 'object' ? s.item?._id : s.item,
    label: (s.item && (s.item.skuDescription || s.item.skuCode)) || 'Sub-item'
  }));

  // Auto-SKU pattern for the selected brand + category (blank = disabled).
  const autoSkuPattern = (() => {
    const sg = selectedBrand?.skuGeneration || {};
    const rules = Array.isArray(sg.categoryRules) ? sg.categoryRules : [];
    const cid = form.category ? String(form.category) : '';
    if (cid) {
      const rule = rules.find(r => r && String(r.category) === cid);
      if (rule) {
        if (rule.enabled === false) return '';
        return String(rule.pattern || sg.defaultPattern || '').trim();
      }
    }
    return String(sg.defaultPattern || '').trim();
  })();

  // Sub-item (add-on) builder state
  const [subItemToAdd, setSubItemToAdd] = useState(null);
  const [subItemQtyToAdd, setSubItemQtyToAdd] = useState(1);
  const [subItemOptions, setSubItemOptions] = useState([]);
  const [subItemLoading, setSubItemLoading] = useState(false);

  // Progress modal state
  const [progressModal, setProgressModal] = useState({
    isOpen: false,
    title: '',
    steps: [],
    currentStep: 0,
    progress: 0,
    message: ''
  });

  // UpdatedBy modal state
  const [updatedByModal, setUpdatedByModal] = useState({
    isOpen: false,
    item: null
  });

  // Excel import (pricelist) state
  const [showImport, setShowImport] = useState(false);
  const [importBrand, setImportBrand] = useState(null);
  const [importCategory, setImportCategory] = useState(null);
  const [importSeries, setImportSeries] = useState({ value: 'RS', label: 'Regular Stock (RS)' });
  const [applyGstToOffers, setApplyGstToOffers] = useState(false);
  const [gstOfferTypes, setGstOfferTypes] = useState([]);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [strictImport, setStrictImport] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  // Excel preview state
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewViewMode, setPreviewViewMode] = useState('flat');
  const [previewChecked, setPreviewChecked] = useState(false);

  // QR Code generation state
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrItem, setQrItem] = useState(null);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState(null);
  const [qrName, setQrName] = useState(null);
  const [companyPublicUrl, setCompanyPublicUrl] = useState('');

  // QR Scanner state
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [qrScannerLoading, setQrScannerLoading] = useState(false);
  const [availableCameras, setAvailableCameras] = useState([]);
  const [selectedCameraId, setSelectedCameraId] = useState('');

  // Toast notification state
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');

  // Convert File to Data URL for draft storage
  const convertImageToDataUrl = useCallback(async (file) => {
    return new Promise((resolve, reject) => {
      if (!file || typeof file === 'string') {
        resolve(file); // Already a string or null
        return;
      }
      
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }, []);

  // Convert Data URL back to File object (for form submission)
  const convertDataUrlToFile = useCallback(async (dataUrl, filename, fileSize) => {
    if (!dataUrl || typeof dataUrl !== 'string' || !dataUrl.startsWith('data:')) {
      return dataUrl; // Not a data URL
    }

    try {
      const response = await fetch(dataUrl);
      const blob = await response.blob();
      
      // Create a File object from the blob
      const file = new File([blob], filename || 'image.jpg', {
        type: blob.type,
        lastModified: Date.now()
      });
      
      console.log('🔄 Converted data URL back to File:', file.name, file.size);
      return file;
    } catch (error) {
      console.error('Error converting data URL to File:', error);
      return dataUrl; // Return original if conversion fails
    }
  }, []);

  // Prepare form data for draft saving (convert images)
  const prepareFormDataForDraft = useCallback(async (formData) => {
    try {
      const draftData = { ...formData };
      
      // Convert image File to data URL for storage
      if (draftData.image instanceof File) {
        console.log('🖼️ Converting image File to data URL for draft');
        draftData.image = await convertImageToDataUrl(draftData.image);
        draftData._imageFileName = formData.image.name; // Store original filename
        draftData._imageFileSize = formData.image.size; // Store file size
      }
      
      return draftData;
    } catch (error) {
      console.error('Error preparing form data for draft:', error);
      return formData;
    }
  }, [convertImageToDataUrl]);

  // Initialize draft manager with image handling
  const draftManager = useDraftManager({
    userId: user?.user?._id,
    companyId: user?.user?.company?._id || user?.user?.company,
    featureType: 'itemManagement',
    formData: form,
    setFormData: setForm,
    prepareFormData: prepareFormDataForDraft, // Custom data preparation
    onError: (error) => {
      console.error('Draft error:', error);
    },
    onDraftSaved: (savedDraft, isManual) => {
      const hasImage = savedDraft.draftData?.image && (
        savedDraft.draftData.image instanceof File || 
        typeof savedDraft.draftData.image === 'string'
      );
      const imageInfo = hasImage ? ' (including image)' : '';
      const saveType = isManual ? 'manually' : 'automatically';
      
      setNotificationMessage(`Draft saved ${saveType}${imageInfo}`);
      setShowNotification(true);
      
      // Update image preview if draft contains image data URL
      if (hasImage && typeof savedDraft.draftData.image === 'string') {
        console.log('🖼️ Updating image preview from saved draft data URL');
        setImagePreview(savedDraft.draftData.image);
      }
    },
    onDraftLoaded: (loadedDraft) => {
      console.log('🔷 Draft loaded, waiting for brands...', loadedDraft);
      
      // Check if draft contains image data
      if (loadedDraft.draftData?.image && typeof loadedDraft.draftData.image === 'string') {
        console.log('🖼️ Draft contains image data:', loadedDraft.draftData._imageFileName || 'Unknown filename');
        
        // Show notification about image loading
        setTimeout(() => {
          setNotificationMessage(`Draft loaded with saved image: ${loadedDraft.draftData._imageFileName || 'image file'}`);
          setShowNotification(true);
        }, 1000); // Delay to show after initial loading
      }
      
      // Don't handle here, let the effect below handle it after brands are loaded
    }
  });

  // Handle draft loading after brands are available
  useEffect(() => {
    if (!brands || brands.length === 0) {
      console.log('⏳ Waiting for brands to load...');
      return;
    }

    // Only process if we have draft data in the form
    if (!form.brand && !form.category) {
      return;
    }

    console.log('🔷 Processing draft with brands available', { formBrand: form.brand, formCategory: form.category });

    // Handle brand selection from draft
    if (form.brand) {
      const brandOption = brands.find(b => b.value === form.brand);
      console.log('🔷 Found brand option:', brandOption);
      if (brandOption) {
        setSelectedBrand(brandOption);
        setBrandAdditionalFields(brandOption.additionalFields || []);

        // Handle category selection from draft
        if (form.category && brandOption.categories) {
          const categoryData = brandOption.categories.find(c => c._id === form.category);
          console.log('🔷 Found category data:', categoryData);
          if (categoryData) {
            const categoryOption = { value: categoryData._id, label: categoryData.name };
            setSelectedCategory(categoryOption);
          }
        }
      }
    }

    // Handle image preview from draft
    if (form.image && typeof form.image === 'string') {
      console.log('🖼️ Setting image preview from draft data URL');
      setImagePreview(form.image);
    }
  }, [brands, form.brand, form.category, form.image]); // Run when brands load or form data changes

  // Auto-hide notification after 3 seconds
  useEffect(() => {
    if (showNotification) {
      const timer = setTimeout(() => {
        setShowNotification(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showNotification]);

  const toggleUpdateDetails = (updateId) => {
    setExpandedUpdates(prev => ({
      ...prev,
      [updateId]: !prev[updateId]
    }));
  };

  // Enhanced CSS for animations, scrollbars, and dark mode effects
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes modalSlideIn {
        from {
          opacity: 0;
          transform: scale(0.95) translateY(-10px);
        }
        to {
          opacity: 1;
          transform: scale(1) translateY(0);
        }
      }
      
      @keyframes slideInFromRight {
        from {
          opacity: 0;
          transform: translateX(100%);
        }
        to {
          opacity: 1;
          transform: translateX(0);
        }
      }
      
      @keyframes fadeIn {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }
      
      .animate-pulse-scale {
        animation: modalSlideIn 0.4s cubic-bezier(0.4, 0, 0.2, 1);
      }
      
      .animate-fade-in {
        animation: fadeIn 0.3s ease-out;
      }
      
      /* Enhanced scrollbar styles */
      .scrollbar-light::-webkit-scrollbar {
        width: 8px;
        height: 8px;
      }
      .scrollbar-light::-webkit-scrollbar-track {
        background: #f8fafc;
        border-radius: 4px;
      }
      .scrollbar-light::-webkit-scrollbar-thumb {
        background: #cbd5e1;
        border-radius: 4px;
        border: 2px solid #f8fafc;
      }
      .scrollbar-light::-webkit-scrollbar-thumb:hover {
        background: #94a3b8;
      }
      .scrollbar-light::-webkit-scrollbar-corner {
        background: #f8fafc;
      }
      
      .scrollbar-dark::-webkit-scrollbar {
        width: 8px;
        height: 8px;
      }
      .scrollbar-dark::-webkit-scrollbar-track {
        background: #1f2937;
        border-radius: 4px;
      }
      .scrollbar-dark::-webkit-scrollbar-thumb {
        background: #4b5563;
        border-radius: 4px;
        border: 2px solid #1f2937;
      }
      .scrollbar-dark::-webkit-scrollbar-thumb:hover {
        background: #6b7280;
      }
      .scrollbar-dark::-webkit-scrollbar-corner {
        background: #1f2937;
      }
      
      /* Dark mode transitions */
      * {
        transition: background-color 0.2s ease-in-out, border-color 0.2s ease-in-out, color 0.2s ease-in-out;
      }
      
      /* Custom focus styles for dark mode */
      .dark-focus:focus {
        outline: none;
        box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.4);
      }
      
      .light-focus:focus {
        outline: none;
        box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.3);
      }

      /* Web image search picker */
      .web-image-grid {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 12px;
        margin-top: 10px;
        max-height: 55vh;
        overflow-y: auto;
        padding: 4px;
      }
      @media (max-width: 640px) {
        .web-image-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
      }
      .web-image-card {
        border-radius: 10px;
        overflow: hidden;
        cursor: pointer;
        border: 2px solid transparent;
        background: #f8fafc;
        transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
      }
      .web-image-card:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
      }
      .web-image-card.selected {
        border-color: #6366F1;
        box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.35);
      }
      .web-image-thumb-wrap {
        width: 100%;
        aspect-ratio: 1 / 1;
        display: flex;
        align-items: center;
        justify-content: center;
        background: #ffffff;
      }
      .web-image-thumb {
        max-width: 100%;
        max-height: 100%;
        object-fit: contain;
      }
      .web-image-title {
        font-size: 11px;
        color: #475569;
        padding: 6px 8px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        margin: 0;
        text-align: center;
      }
      .web-image-grid.dark .web-image-card { background: #374151; }
      .web-image-grid.dark .web-image-thumb-wrap { background: #1f2937; }
      .web-image-grid.dark .web-image-title { color: #d1d5db; }
      .web-image-source {
        display: block;
        margin: 0 auto;
        max-width: calc(100% - 16px);
        font-size: 10px;
        color: #4f46e5;
        padding: 0 0 7px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        cursor: pointer;
      }
      .web-image-source:hover { text-decoration: underline; }
      .web-image-grid.dark .web-image-source { color: #818cf8; }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Helper function to check if a value is meaningful (not empty, zero, or '00000')
  const isValidValue = (value) => {
    if (!value) return false;
    if (value === null || value === undefined) return false;
    const stringValue = String(value).trim();
    if (stringValue === '' || stringValue === '0' || stringValue === '00000' || stringValue === 'null' || stringValue === 'undefined') return false;
    if (Number(value) === 0) return false;
    return true;
  };

  // Helper function to display value or fallback
  const displayValue = (value, fallback = 'N/A') => {
    return isValidValue(value) ? value : fallback;
  };

  // Default offer types (used when a brand has none configured yet)
  const DEFAULT_OFFER_TYPES = [
    { key: 'nrp', label: 'NRP' },
    { key: 'clp', label: 'CLP' },
    { key: 'sdp', label: 'SDP' },
    { key: 'kap', label: 'KAP' },
    { key: 'hvr', label: 'HVR' },
    { key: 'spl', label: 'SPL' },
    { key: 'flat', label: 'Flat' }
  ];

  // Returns the active offer types for a brand (key + label), falling back to
  // the default set when the brand has no offerTypes configured.
  const getActiveOfferTypes = (brand) => {
    const ots = brand?.offerTypes && brand.offerTypes.length
      ? brand.offerTypes.filter(ot => ot.active !== false)
      : DEFAULT_OFFER_TYPES;
    // Ensure 'flat' (special = MRP) is always present and last
    const withoutFlat = ots.filter(ot => ot.key !== 'flat');
    const flat = ots.find(ot => ot.key === 'flat');
    if (!flat) withoutFlat.push({ key: 'flat', label: 'Flat' });
    else withoutFlat.push(flat);
    return withoutFlat;
  };

  // Series options
  const seriesOptions = [
    { value: 'RS', label: 'Regular Stock (RS)' },
    { value: 'AO', label: 'Against Order (AO)' },
    { value: 'SC', label: 'Stock Clearance (SC)' },
    { value: 'DC', label: 'Discontinued (DC)' },
    { value: 'ITF', label: 'Item to Focus (ITF)' },
    { value: 'OT', label: 'One Time (OT)' }
  ];

  // Limit options for react-select
  const limitOptions = [
    { value: 5, label: '5' },
    { value: 10, label: '10' },
    { value: 25, label: '25' },
    { value: 50, label: '50' },
    { value: 100, label: '100' }
  ];

  const totalPages = Math.ceil(totalItems / limit);

  // Fetch items - useCallback so it can be called from other handlers (e.g., import)
  const fetchItems = useCallback(async () => {
    try {
      const brand = listBrand ? listBrand.value : undefined;
      const total = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/items/count`, {
        params: { filter, brand },
      });
      setTotalItems(total.data.count);
      if (total.data.count === 0 || total.data.count < (page - 1) * limit) {
        setPage(1);
      }
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/items`, {
        params: { page, limit, filter, brand },
      });
      setItems(response.data);
      setFilteredItems(response.data);
    } catch (error) {
      Swal.fire('Error', 'Failed to fetch items :- ' + error.message, 'error');
    }
  }, [page, limit, filter, listBrand]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // Fetch brands
  const fetchBrands = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/brands/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.success && Array.isArray(response.data.brands)) {
        // Transform brands data for react-select and include additional fields
        const brandOptions = response.data.brands.map(brand => ({
          value: brand._id,
          label: brand.name,
          categories: brand.categories || [],
          additionalFields: brand.additionalFields || [],
          offerTypes: brand.offerTypes || [],
          skuGeneration: brand.skuGeneration || {},
          advancedItems: !!brand.advancedItems,
          accessoriesEnabled: !!brand.advancedItems && !!brand.accessoriesEnabled,
          _id: brand._id  // Keep the full brand ID for reference
        }));
        setBrands(brandOptions);
        
        console.log('Fetched brands with additional fields:', brandOptions);
      } else {
        console.error('Invalid brands data:', response.data);
        setBrands([]);
      }
    } catch (error) {
      console.error('Error fetching brands:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch brands'
      });
      setBrands([]);
    } finally {
      // Add a small delay to ensure react-select options are fully loaded
      setTimeout(() => {
        setLoading(false);
      }, 200);
    }
  };

  useEffect(() => {
    fetchBrands();
  }, []);

  // Handle brand selection
  const handleBrandChange = (selectedOption) => {
    console.log('Brand changed:', selectedOption);
    console.log('Available brands:', brands);
    
    setSelectedBrand(selectedOption);
    setSelectedCategory(null); // Reset category when brand changes
    
    // Find the brand and get its additional fields
    const brand = brands.find(b => b.value === selectedOption?.value);
    console.log('Found brand:', brand);
    console.log('Brand additional fields:', brand?.additionalFields);
    
    setBrandAdditionalFields(brand?.additionalFields || []);
    
    // Reset additional field values when brand changes
    const newAdditionalFieldValues = {};
    if (brand?.additionalFields && brand.additionalFields.length > 0) {
      brand.additionalFields.forEach(field => {
        newAdditionalFieldValues[field.fieldName] = field.defaultValue || '';
      });
    }

    // Initialise offerType keys from the brand's (active) offer types so the
    // dynamic offer-type inputs have controlled state. `flat` is reserved.
    const newOfferType = {};
    getActiveOfferTypes(brand).forEach(ot => {
      if (ot.key === 'flat') return;
      newOfferType[ot.key] = '';
    });
    
    setForm(prev => ({ 
      ...prev, 
      brand: selectedOption?.value, 
      category: '',
      additionalFieldValues: newAdditionalFieldValues,
      offerType: newOfferType
    }));
    console.log('Set additional fields:', brand?.additionalFields || []);
    console.log('New additional field values:', newAdditionalFieldValues);
  };

  // Handle category selection
  const handleCategoryChange = (selectedOption) => {
    setSelectedCategory(selectedOption);
    setForm(prev => ({ ...prev, category: selectedOption?.value }));
  };

  // Handle additional field value changes
  const handleAdditionalFieldValueChange = (fieldName, value) => {
    setForm(prev => ({
      ...prev,
      additionalFieldValues: {
        ...prev.additionalFieldValues,
        [fieldName]: value
      }
    }));
  };

  // ── Sub-item (add-on) helpers ─────────────────────────────────────────
  // Search existing items to add as sub-items. Excludes the item being edited.
  const searchSubItemOptions = useCallback(async (inputValue) => {
    setSubItemLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/items`, {
        params: { filter: inputValue || '', limit: 20, page: 0 }
      });
      const currentId = editingId || form._id;
      setSubItemOptions((response.data || [])
        .filter(it => it._id !== currentId)
        .map(it => ({
          value: it._id,
          label: `${it.skuCode}${it.skuDescription ? ` - ${it.skuDescription}` : ''}`,
          item: it
        })));
    } catch (error) {
      console.error('Sub-item search failed:', error);
      setSubItemOptions([]);
    } finally {
      setSubItemLoading(false);
    }
  }, [editingId, form._id]);

  const handleAddSubItem = () => {
    if (!subItemToAdd || !subItemToAdd.item) return;
    const chosen = subItemToAdd.item;
    const chosenId = chosen._id;
    const already = (form.subItems || []).some(s => (typeof s.item === 'object' ? s.item._id : s.item) === chosenId);
    if (already) {
      toast.info('That item is already a sub-item.');
      return;
    }
    // Store only the fields we display, keeping drafts light. Keep the
    // referenced item's variant names so a variant can be pinned per row.
    setForm(prev => ({
      ...prev,
      subItems: [
        ...(prev.subItems || []),
        {
          item: {
            _id: chosen._id,
            skuCode: chosen.skuCode,
            skuDescription: chosen.skuDescription,
            variants: (chosen.variants || []).map(v => v.name).filter(Boolean)
          },
          quantity: Math.max(1, parseInt(subItemQtyToAdd, 10) || 1),
          variantName: ''
        }
      ]
    }));
    setSubItemToAdd(null);
    setSubItemQtyToAdd(1);
  };

  const handleSubItemQtyChange = (index, qty) => {
    setForm(prev => ({
      ...prev,
      subItems: (prev.subItems || []).map((s, i) =>
        i === index ? { ...s, quantity: Math.max(1, parseInt(qty, 10) || 1) } : s
      )
    }));
  };

  const handleSubItemVariantChange = (index, value) => {
    setForm(prev => ({
      ...prev,
      subItems: (prev.subItems || []).map((s, i) =>
        i === index ? { ...s, variantName: value } : s
      )
    }));
  };

  const handleRemoveSubItem = (index) => {
    setForm(prev => ({
      ...prev,
      subItems: (prev.subItems || []).filter((_, i) => i !== index)
    }));
  };

  // ── Variant (price list) helpers ──────────────────────────────────────
  const emptyVariant = () => ({
    variantId: '', name: '', additionalFieldValues: {}, mrp: '', offerType: {},
    preferredSubItems: [], isActive: true
  });

  const handleAddVariant = () => {
    // Auto-select all of the item's current sub-items as preferred by default;
    // the user can deselect any that don't apply to this variant.
    const allSubIds = (form.subItems || [])
      .map(s => (typeof s.item === 'object' ? s.item?._id : s.item))
      .filter(Boolean);
    setForm(prev => ({ ...prev, variants: [...(prev.variants || []), { ...emptyVariant(), preferredSubItems: allSubIds }] }));
  };

  const handleRemoveVariant = (index) => {
    setForm(prev => ({ ...prev, variants: (prev.variants || []).filter((_, i) => i !== index) }));
  };

  const handleVariantChange = (index, field, value) => {
    setForm(prev => ({
      ...prev,
      variants: (prev.variants || []).map((v, i) => i === index ? { ...v, [field]: value } : v)
    }));
  };

  const handleVariantAdditionalFieldChange = (index, fieldName, value) => {
    setForm(prev => ({
      ...prev,
      variants: (prev.variants || []).map((v, i) =>
        i === index ? { ...v, additionalFieldValues: { ...v.additionalFieldValues, [fieldName]: value } } : v
      )
    }));
  };

  const handleVariantOfferTypeChange = (index, key, value) => {
    setForm(prev => ({
      ...prev,
      variants: (prev.variants || []).map((v, i) =>
        i === index ? { ...v, offerType: { ...v.offerType, [key]: value } } : v
      )
    }));
  };

  const handleVariantPreferredChange = (index, selectedOptions) => {
    setForm(prev => ({
      ...prev,
      variants: (prev.variants || []).map((v, i) =>
        i === index ? { ...v, preferredSubItems: selectedOptions ? selectedOptions.map(o => o.value) : [] } : v
      )
    }));
  };

  // Download the selected web image through the backend proxy and attach it to the form
  const applyWebImage = async (webImage) => {
    Swal.fire({
      title: 'Fetching selected image...',
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => Swal.showLoading()
    });

    try {
      const proxyUrl = `${process.env.REACT_APP_API_BASE_URL}/items/web-image-proxy?url=${encodeURIComponent(webImage.original)}`;
      const response = await fetch(proxyUrl);
      if (!response.ok) throw new Error('Failed to download the selected image');

      const blob = await response.blob();
      if (!blob.type.startsWith('image/')) throw new Error('Selected URL is not a valid image');
      if (blob.size > 5 * 1024 * 1024) throw new Error('Selected image exceeds the 5MB limit');

      const extension = (blob.type.split('/')[1] || 'jpg').replace('jpeg', 'jpg');
      const file = new File([blob], `web-image-${Date.now()}.${extension}`, { type: blob.type });

      setForm(prev => ({ ...prev, image: file, _imageFileName: null, _imageFileSize: null }));
      setImagePreview(URL.createObjectURL(file));

      Swal.close();
      setNotificationMessage(`Web image applied (${Math.round(blob.size / 1024)} KB)`);
      setShowNotification(true);
    } catch (error) {
      console.error('Error applying web image:', error);
      Swal.close();
      Swal.fire('Error', error.message || 'Failed to apply web image', 'error');
    }
  };

  // Show found web images and ask the user if any of them are usable
  const openWebImagePicker = async (images) => {
    let selectedIndex = null;

    // Human-friendly source label (domain name)
    const sourceLabel = (url) => {
      try { return new URL(url).hostname.replace(/^www\./, ''); } catch (_) { return 'Source'; }
    };

    const galleryHtml = `
      <div class="web-image-grid ${darkMode ? 'dark' : ''}">
        ${images.map((img, index) => `
          <div class="web-image-card" data-web-image-index="${index}" title="${(img.title || '').replace(/"/g, '&quot;')}">
            <div class="web-image-thumb-wrap">
              <img class="web-image-thumb" src="${process.env.REACT_APP_API_BASE_URL}/items/web-image-proxy?url=${encodeURIComponent(img.thumbnail || img.original)}" alt="Result ${index + 1}" loading="lazy" />
            </div>
            <p class="web-image-title">${(img.title || `Option ${index + 1}`).slice(0, 60)}</p>
            ${img.source ? `<a class="web-image-source" href="${img.source}" target="_blank" rel="noopener noreferrer" title="Open where this image was found">🔗 ${sourceLabel(img.source)}</a>` : ''}
          </div>
        `).join('')}
      </div>
    `;

    const result = await Swal.fire({
      title: 'Is any of these usable?',
      text: 'Click an image to select it, then confirm to use it.',
      html: galleryHtml,
      showDenyButton: true,
      showCancelButton: true,
      denyButtonText: 'Search Again',
      confirmButtonText: 'Use Selected Image',
      cancelButtonText: 'None of these',
      reverseButtons: true,
      focusConfirm: false,
      allowOutsideClick: () => !Swal.isLoading(),
      didOpen: () => {
        const container = Swal.getHtmlContainer();
        const confirmBtn = Swal.getConfirmButton();
        if (confirmBtn) confirmBtn.disabled = true;

        // Silently drop cards whose thumbnail fails to load
        container.querySelectorAll('.web-image-thumb').forEach((thumbEl) => {
          thumbEl.addEventListener('error', () => {
            const card = thumbEl.closest('.web-image-card');
            if (card) card.remove();
            if (!container.querySelector('.web-image-card')) {
              Swal.close();
              Swal.fire({ icon: 'info', title: 'No Results', text: 'None of the found images could be loaded. Try a different search.' });
            }
          });
        });

        container.querySelectorAll('[data-web-image-index]').forEach((card) => {
          card.addEventListener('click', (e) => {
            // Let source links open in a new tab without selecting the image
            if (e.target.closest('a')) return;
            selectedIndex = parseInt(card.getAttribute('data-web-image-index'), 10);
            container.querySelectorAll('.web-image-card.selected').forEach(c => c.classList.remove('selected'));
            card.classList.add('selected');
            if (confirmBtn) confirmBtn.disabled = false;
          });
        });
      },
      preConfirm: () => {
        if (selectedIndex === null) {
          Swal.showValidationMessage('Please select an image first');
          return false;
        }
        return selectedIndex;
      }
    });

    if (result.isConfirmed && result.value !== null && result.value !== undefined) {
      await applyWebImage(images[result.value]);
    } else if (result.isDenied) {
      // Let the user refine the query and search again
      await handleWebImageSearch();
    } else if (result.isDismissed) {
      setNotificationMessage('No web image selected - you can upload manually');
      setShowNotification(true);
    }
  };

  // Entry point: search product images automatically on the web
  const handleWebImageSearch = async () => {
    if (isWebImageSearching) return;

    // Description carries the meaningful keywords; engines weight earlier terms higher
    const defaultQuery = [form.skuDescription, form.skuCode].filter(Boolean).join(' ').trim();

    // Confirm / edit the search query before searching
    const { value: query } = await Swal.fire({
      title: 'Auto-Find Image on Web',
      input: 'text',
      inputLabel: 'Search query (brand & description)',
      inputValue: defaultQuery,
      inputPlaceholder: 'e.g. Havells ceiling fan',
      showCancelButton: true,
      confirmButtonText: 'Search Web',
      cancelButtonText: 'Cancel',
      inputValidator: (value) => (!value || !value.trim()) ? 'Please enter a search query' : null,
      didOpen: () => {
        const input = Swal.getInput();
        if (input) input.select();
      }
    });

    if (!query || !query.trim()) return;

    setIsWebImageSearching(true);
    Swal.fire({
      title: 'Searching the web...',
      html: `Looking for images of "<strong>${query.trim().replace(/</g, '&lt;')}</strong>"`,
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => Swal.showLoading()
    });

    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/items/search-web-images`, {
        params: { q: query.trim(), limit: 9 }
      });

      const images = response.data?.images || [];
      if (!images.length) {
        Swal.fire('No Results', 'No usable images were found. Please try another search.', 'info');
        return;
      }

      await openWebImagePicker(images);
    } catch (error) {
      console.error('Web image search failed:', error);
      const message = error.response?.data?.error || error.message || 'Failed to search images on the web';
      Swal.close();
      Swal.fire('Error', message, 'error');
    } finally {
      setIsWebImageSearching(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    console.log('🚀 Form submission started');
    console.log('📋 Current form state:', form);
    console.log('🔧 Additional field values:', form.additionalFieldValues);
    console.log('🏷️ Brand additional fields:', brandAdditionalFields);

    // Show progress modal
    const steps = isEditing 
      ? ['Preparing update', 'Processing image', 'Tracking changes', 'Updating item', 'Saving changes']
      : ['Preparing data', 'Processing image', 'Creating item', 'Saving item'];
      
    setProgressModal({
      isOpen: true,
      title: isEditing ? 'Updating Item' : 'Creating Item',
      steps,
      currentStep: 0,
      progress: 0,
      message: 'Initializing...'
    });

    try {
      const formData = new FormData();
      
      // Update progress
      setProgressModal(prev => ({
        ...prev,
        currentStep: 1,
        progress: 20,
        message: 'Preparing form data...'
      }));

      // Handle image conversion from draft data URL if needed
      let imageToSubmit = form.image;
      if (form.image && typeof form.image === 'string' && form.image.startsWith('data:')) {
        console.log('🔄 Converting draft image data URL to File for submission');
        setProgressModal(prev => ({
          ...prev,
          message: 'Converting draft image for upload...'
        }));
        
        imageToSubmit = await convertDataUrlToFile(
          form.image, 
          form._imageFileName || 'image.jpg', 
          form._imageFileSize
        );
      }

      Object.keys(form).forEach((key) => {
        if (key === 'image' && imageToSubmit) {
          formData.append(key, imageToSubmit);
        } else if (key === '_imageFileName' || key === '_imageFileSize') {
          // Skip metadata fields, don't send to server
        } else if (key === 'offerType') {
          const offerType = { ...form[key], flat: form.mrp };
          formData.append(key, JSON.stringify(offerType));
        } else if (key === 'additionalFieldValues') {
          console.log('📝 Adding additionalFieldValues to form:', form[key]);
          formData.append(key, JSON.stringify(form[key]));
        } else if (key === 'subItems') {
          // Send only the item id + quantity; the backend validates the ids.
          const subItems = (form.subItems || []).map(s => ({
            item: typeof s.item === 'object' ? s.item?._id : s.item,
            quantity: Number(s.quantity) || 1,
            variantName: s.variantName || ''
          }));
          formData.append(key, JSON.stringify(subItems));
        } else if (key === 'variants') {
          // Variant price list (flat = mrp applied on the server).
          const variants = (form.variants || []).map(v => ({
            variantId: v.variantId || '',
            name: v.name || '',
            additionalFieldValues: v.additionalFieldValues || {},
            mrp: Number(v.mrp) || 0,
            offerType: v.offerType || {},
            imageId: v.imageId || undefined,
            preferredSubItems: (v.preferredSubItems || []).filter(Boolean),
            isDefault: !!v.isDefault,
            isActive: v.isActive !== false
          }));
          formData.append(key, JSON.stringify(variants));
        } else {
          formData.append(key, form[key]);
        }
      });

      const keptOtherIds = editingOtherImageIds.filter(id => !otherImageRemoved.map(String).includes(String(id)));
      formData.append('otherImageIds', JSON.stringify(keptOtherIds));
      formData.append('removeOtherImageIds', JSON.stringify(otherImageRemoved));
      if (primaryOtherImageId) formData.append('primaryOtherImageId', String(primaryOtherImageId));
      otherImageFiles.forEach(file => formData.append('otherImages', file));
      const variantImageIndexes = [];
      (form.variants || []).forEach((variant, index) => {
        if (variant.imageFile) {
          formData.append('variantImages', variant.imageFile);
          variantImageIndexes.push(index);
        }
      });
      formData.append('variantImageIndexes', JSON.stringify(variantImageIndexes));

      if (!isEditing) {
        formData.append('createdBy', user.user._id);
      } else {
        formData.append('updatedBy', user.user._id);
      }

      // Update progress
      setProgressModal(prev => ({
        ...prev,
        currentStep: 2,
        progress: 40,
        message: form.image ? 'Processing image upload...' : 'Processing data...'
      }));

      let response;
      if (isEditing) {
        setProgressModal(prev => ({
          ...prev,
          currentStep: 3,
          progress: 60,
          message: 'Analyzing changes and updating item...'
        }));
        
        response = await axios.put(`${process.env.REACT_APP_API_BASE_URL}/items/${editingId}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        setProgressModal(prev => ({
          ...prev,
          currentStep: 3,
          progress: 70,
          message: 'Creating new item...'
        }));
        
        response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/items`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      // Update progress
      setProgressModal(prev => ({
        ...prev,
        currentStep: steps.length - 1,
        progress: 90,
        message: 'Finalizing...'
      }));

      // Show success information from response
      if (response.data.success) {
        let successMessage = isEditing ? 'Item updated successfully' : 'Item created successfully';
        
        if (response.data.imageResult) {
          if (response.data.imageResult.isDuplicate) {
            successMessage += '\n\nImage: Found existing image and linked to item';
          } else {
            successMessage += '\n\nImage: New image processed and saved';
          }
        }
        
        if (response.data.changedFields && response.data.changedFields.length > 0) {
          successMessage += `\n\nChanges tracked: ${response.data.changedFields.join(', ')}`;
        }

        // Delete the draft after successful item creation/update
        if (draftManager.currentDraftId) {
          try {
            await draftManager.deleteDraft(draftManager.currentDraftId);
            console.log('✅ Draft deleted after successful item save');
          } catch (draftError) {
            console.error('⚠️ Failed to delete draft:', draftError);
            // Don't show error to user, draft deletion is not critical
          }
        }

        // Complete progress
        setProgressModal(prev => ({
          ...prev,
          progress: 100,
          message: 'Complete!'
        }));

        setTimeout(() => {
          setProgressModal(prev => ({ ...prev, isOpen: false }));
          Swal.fire({
            title: 'Success',
            text: successMessage,
            icon: 'success',
            confirmButtonText: 'OK'
          });
          resetForm();
        }, 1000);
      }

    } catch (error) {
      console.error('Submit error:', error);
      
      setProgressModal(prev => ({ ...prev, isOpen: false }));
      
      let errorMessage = 'Failed to save item';
      if (error.response?.data?.details) {
        errorMessage += `\n\nDetails: ${error.response.data.details}`;
      }
      
      Swal.fire('Error', errorMessage, 'error');
    }
  };

  const evaluateRuleCondition = (cond, values) => {
    if (!cond || !cond.field) return false;
    const raw = values?.[cond.field];
    const text = raw === undefined || raw === null ? '' : String(raw).trim();
    const op = cond.operator || 'equals';
    if (op === 'notEmpty') return text !== '';
    if (op === 'empty') return text === '';
    if (op === 'equals') return text.toLowerCase() === String(cond.value ?? '').trim().toLowerCase();
    if (op === 'in') return (cond.values || []).map(String).map(v => v.toLowerCase()).includes(text.toLowerCase());
    if (op === 'gt' || op === 'gte' || op === 'lt' || op === 'lte') {
      const n = Number(text);
      const v = Number(cond.value);
      if (!Number.isFinite(n) || !Number.isFinite(v)) return false;
      return op === 'gt' ? n > v : op === 'gte' ? n >= v : op === 'lt' ? n < v : n <= v;
    }
    return false;
  };
  const isFieldRequiredNow = (field, values) => {
    if (field.isRequired) return true;
    const rule = field.requiredWhen;
    if (!rule) return false;
    const conditions = Array.isArray(rule) ? rule : (Array.isArray(rule.conditions) ? rule.conditions : []);
    if (!conditions.length) return false;
    const mode = rule.mode || 'all';
    return mode === 'any'
      ? conditions.some(c => evaluateRuleCondition(c, values))
      : conditions.every(c => evaluateRuleCondition(c, values));
  };
  const missingRequiredFields = (fields, values) => (fields || [])
    .filter(f => !f.derived && isFieldRequiredNow(f, values) && !String(values?.[f.fieldName] ?? '').trim())
    .map(f => f.fieldName);

  const validateForm = () => {
    const requiredFields = [
      'brand',
      'category',
      'series',
      'hsnCode',
      'gstRate',
      'mrp',
    ];
    // When auto-SKU is configured for the category, SKU + description may be
    // left blank (the server generates them).
    if (!autoSkuPattern) requiredFields.push('skuCode', 'skuDescription');

    if (requiredFields.some((field) => !form[field])) {
      Swal.fire('Warning', 'All fields are required', 'warning');
      return false;
    }

    if (form.image && form.image.type && !form.image.type.startsWith('image/')) {
      Swal.fire('Warning', 'Image must be a valid image file', 'warning');
      return false;
    }

    const missing = [
      ...missingRequiredFields(visibleAdditionalFields, form.additionalFieldValues),
      ...(isAccessoryContext ? missingRequiredFields(visibleAccessoryFields, form.additionalFieldValues) : [])
    ];
    if (missing.length > 0) {
      Swal.fire('Warning', `Required field(s): ${Array.from(new Set(missing)).join(', ')}`, 'warning');
      return false;
    }
    return true;
  };

  // Function to preview/check Excel file before import
  const handleImportFile = (file) => {
    if (!file) {
      setImportFile(null);
    } else {
      const allowedTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel'
      ];
      if (file.type && !allowedTypes.includes(file.type)) {
        toast.error('Please select a valid Excel file (.xlsx or .xls)');
        return false;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File too large. Please choose a file smaller than 10MB.');
        return false;
      }
      if (file.size === 0) {
        toast.error('Selected file appears to be empty.');
        return false;
      }
      setImportFile(file);
    }
    setPreviewChecked(false);
    setPreviewData(null);
    setShowPreview(false);
    return true;
  };

  // Function to preview/check Excel file before import
  const previewExcelFile = async () => {
    if (!importFile) {
      toast.warning('Please select an Excel file first.');
      return;
    }
    if (!importBrand) {
      toast.warning('Please select a brand to validate against the correct template.');
      return;
    }

    try {
      toast.info('Reading and validating Excel file...');
      
      // Dynamic import xlsx library
      const XLSX = await import('xlsx');
      
      const file = importFile;
      const arrayBuffer = await file.arrayBuffer();

      // Inspect drawings so broken/non-standard image data is reported before import.
      let drawingNotice = '';
      try {
        const JSZip = (await import('jszip')).default;
        const zip = await JSZip.loadAsync(arrayBuffer);
        const drawingFiles = Object.keys(zip.files).filter(n => /^xl\/drawings\/[^/]+\.xml$/.test(n));
        let repairable = false;
        let imageCount = 0;
        for (const name of drawingFiles) {
          const xml = await zip.file(name).async('string');
          imageCount += (xml.match(/<(?:xdr:)?(?:oneCellAnchor|twoCellAnchor)/g) || []).length;
          if (/<wsDr\b/.test(xml) && !/xmlns:xdr=/.test(xml)) repairable = true;
        }
        if (repairable) {
          drawingNotice = `Images use a non-standard drawing format (${imageCount} found). The importer will repair them and import the images. If repair fails, the brand/parent image will be used.`;
        }
      } catch (e) {
        // ignore inspection errors; the import itself will report them
      }
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });

      // Simple 3-tab format: Products / Variants / Accessories.
      const bundleNames = ['Products', 'Variants', 'Accessories'];
      if (bundleNames.some(name => workbook.SheetNames.includes(name))) {
        const readTab = (name) => {
          const sheet = workbook.Sheets[name];
          if (!sheet) return { headers: [], rows: [] };
          const json = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
          return {
            headers: (json[0] || []).map(h => String(h || '').trim()),
            rows: json.slice(1).filter(row => row.some(c => c !== '' && c !== null && c !== undefined))
          };
        };
        const products = readTab('Products');
        const variants = readTab('Variants');
        const accessories = readTab('Accessories');
        const rowIssues = [];
        const seenSku = new Set();
        const num = (value) => Number(String(value ?? '').replace(/[₹,\s]/g, ''));

        products.rows.forEach((row, index) => {
          const rowNum = index + 2;
          const get = (name) => {
            const col = products.headers.indexOf(name);
            return col >= 0 ? row[col] : '';
          };
          const sku = String(get('SKU Code') || '').trim();
          const desc = String(get('SKU Description') || get('Product Name') || '').trim();
          if (!desc) rowIssues.push({ rowNum, message: `Products row ${rowNum}: Product description is required` });
          if (sku && seenSku.has(sku.toLowerCase())) {
            rowIssues.push({ rowNum, message: `Products row ${rowNum}: Duplicate SKU "${sku}"` });
          }
          if (sku) seenSku.add(sku.toLowerCase());
          const gst = num(get('GST Rate'));
          const mrp = num(get('MRP'));
          if (!Number.isFinite(gst) || gst < 0 || gst > 100) rowIssues.push({ rowNum, message: `Products row ${rowNum}: Invalid GST Rate (0-100)` });
          if (!Number.isFinite(mrp) || mrp <= 0) rowIssues.push({ rowNum, message: `Products row ${rowNum}: Invalid MRP (must be > 0)` });
        });

        variants.rows.forEach((row, index) => {
          const rowNum = index + 2;
          const get = (name) => {
            const col = variants.headers.indexOf(name);
            return col >= 0 ? row[col] : '';
          };
          const parent = String(get('Parent SKU') || '').trim();
          const name = String(get('Variant') || get('Variant Name') || '').trim();
          const id = String(get('Variant ID') || '').trim();
          const mrp = num(get('MRP'));
          if (!parent) rowIssues.push({ rowNum, message: `Variants row ${rowNum}: Parent SKU is required` });
          if (!name && !id) rowIssues.push({ rowNum, message: `Variants row ${rowNum}: Variant name or ID is required` });
          if (!Number.isFinite(mrp) || mrp <= 0) rowIssues.push({ rowNum, message: `Variants row ${rowNum}: Invalid MRP (must be > 0)` });
        });

        accessories.rows.forEach((row, index) => {
          const rowNum = index + 2;
          const get = (name) => {
            const col = accessories.headers.indexOf(name);
            return col >= 0 ? row[col] : '';
          };
          const parent = String(get('Parent SKU') || '').trim();
          const child = String(get('Accessory SKU') || get('Child SKU') || '').trim();
          const qty = get('Qty');
          if (!parent) rowIssues.push({ rowNum, message: `Accessories row ${rowNum}: Parent SKU is required` });
          if (!child) rowIssues.push({ rowNum, message: `Accessories row ${rowNum}: Accessory SKU is required` });
          if (qty !== '' && qty !== undefined && qty !== null) {
            const n = num(qty);
            if (!Number.isFinite(n) || n < 1 || !Number.isInteger(n)) {
              rowIssues.push({ rowNum, message: `Accessories row ${rowNum}: Qty must be a whole number >= 1` });
            }
          }
        });

        const headers = ['Sheet', ...Array.from(new Set([
          ...products.headers, ...variants.headers, ...accessories.headers
        ].filter(Boolean)))];
        const toPreviewRow = (sheetName, tab, row, index) => ({
          rowNum: index + 2,
          data: {
            Sheet: sheetName,
            ...Object.fromEntries(tab.headers.map((h, col) => [h, row[col]]))
          }
        });
        const previewRows = [
          ...products.rows.slice(0, 3).map((row, i) => toPreviewRow('Products', products, row, i)),
          ...variants.rows.slice(0, 3).map((row, i) => toPreviewRow('Variants', variants, row, i)),
          ...accessories.rows.slice(0, 3).map((row, i) => toPreviewRow('Accessories', accessories, row, i))
        ];

        setPreviewData({
          drawingNotice,
          headers,
          totalRows: products.rows.length + variants.rows.length + accessories.rows.length,
          previewRows,
          groups: [],
          missingHeaders: [],
          missingAdditionalHeaders: [],
          extraHeaders: [],
          requiredBaseHeaders: [],
          offerTypeHeadersPresent: [],
          additionalFieldsExpected: [],
          rowIssues
        });
        setShowPreview(true);
        setPreviewChecked(true);
        toast.success(`Preview ready - ${products.rows.length} products, ${variants.rows.length} variants, ${accessories.rows.length} accessories`);
        return;
      }

      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
      
      if (jsonData.length < 2) {
        throw new Error('Excel file is empty or has no data rows');
      }
      
      const headers = jsonData[0].map(h => String(h || '').trim());
      const rows = jsonData.slice(1).filter(row => row.some(cell => cell !== '' && cell !== null && cell !== undefined));
      
      // Define required base headers
      const requiredBaseHeaders = ['SKU Code', 'SKU Description', 'HSN Code', 'GST Rate', 'MRP'];
      
      // Single API call to fetch brand data (offer types + additional fields)
      let brandOfferTypes = [];
      let brandSupportedOfferTypes = [];
      let brandAdditionalFields = [];
      let previewAutoSku = false;
      try {
        const token = localStorage.getItem('token');
        const brandRes = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/brands/${importBrand.value}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const brandData = brandRes.data;
        if (brandData) {
          if (brandData.offerTypes) {
            brandOfferTypes = brandData.offerTypes
              .filter(ot => ot.active !== false && ot.key !== 'flat')
              .map(ot => ({ key: ot.key, label: ot.label || ot.key.toUpperCase() }));
          }
          if (brandData.supportedOfferTypes) {
            brandSupportedOfferTypes = brandData.supportedOfferTypes
              .filter(k => String(k).toLowerCase() !== 'flat');
          }
          if (brandData.additionalFields) {
            // Only the fields applicable to the import category.
            const cid = importCategory?.value;
            brandAdditionalFields = brandData.additionalFields
              .filter(f => !(f.categories && f.categories.length) || (cid && f.categories.map(String).includes(String(cid))))
              .map(f => f.fieldName);
          }
          // Auto-SKU enabled for the import category?
          const sg = brandData.skuGeneration || {};
          const cid = importCategory?.value;
          let autoPattern = '';
          if (cid && Array.isArray(sg.categoryRules)) {
            const rule = sg.categoryRules.find(r => String(r.category) === String(cid));
            if (rule) autoPattern = rule.enabled === false ? '' : (rule.pattern || sg.defaultPattern || '');
          }
          if (!autoPattern) autoPattern = sg.defaultPattern || '';
          previewAutoSku = !!String(autoPattern).trim();
        }
      } catch (e) {
        console.warn('Could not fetch brand data for preview:', e);
      }

      // Offer-type columns in the template are emitted as UPPERCASE keys, and
      // most brands only define `supportedOfferTypes` (no `offerTypes`), so
      // accept both as valid offer-type headers.
      const offerTypeHeaders = Array.from(new Set([
        ...brandOfferTypes.map(ot => ot.key.toUpperCase()),
        ...brandSupportedOfferTypes.map(k => String(k).toUpperCase())
      ]));
      
      // Check for missing required headers
      const missingHeaders = requiredBaseHeaders.filter(h => !headers.includes(h));
      
      const missingAdditionalHeaders = brandAdditionalFields.filter(h => !headers.includes(h));
      // Optional columns recognised by the importer but not required.
      const optionalHeaders = ['Sub Items', 'Series', 'Category', 'Row Type', 'Sub Qty', 'Image', 'Variant', 'Variant ID', 'Preferred Sub-item', 'Active'];
      const extraHeaders = headers.filter(h => 
        !requiredBaseHeaders.includes(h) && 
        !offerTypeHeaders.includes(h) && 
        !brandAdditionalFields.includes(h) &&
        !optionalHeaders.includes(h)
      );

      // Lightweight row-level validation (mirrors the importer) so the user can
      // fix problems before uploading.
      const num = (v) => Number(String(v ?? '').replace(/[₹,\s]/g, ''));
      const colVal = (row, name) => {
        const idx = headers.findIndex(h => h.toLowerCase() === String(name).toLowerCase());
        return idx >= 0 ? row[idx] : undefined;
      };
      const offerHeadersForValidation = Array.from(new Set([
        ...brandOfferTypes.map(ot => ot.key),
        ...brandSupportedOfferTypes
      ])).filter(k => String(k).toLowerCase() !== 'flat');
      const rowIssues = [];
      const seenSku = new Map();
      rows.forEach((row, idx) => {
        const rowNum = idx + 2;
        const sku = String(colVal(row, 'SKU Code') ?? '').trim();
        const desc = String(colVal(row, 'SKU Description') ?? '').trim();
        const rtRaw = String(colVal(row, 'Row Type') ?? '').trim();
        const rt = rtRaw.toLowerCase();
        const isSub = rt.startsWith('sub') || rt.startsWith('add');
        const isVar = rt.startsWith('var');
        if (rtRaw && !isSub && !isVar && rt !== 'item' && rt !== 'base') {
          rowIssues.push({ rowNum, message: `Unknown Row Type "${rtRaw}"` });
          return;
        }
        const sq = colVal(row, 'Sub Qty');
        if (sq !== undefined && sq !== null && sq !== '') {
          const n = num(sq);
          if (!Number.isFinite(n) || n < 1 || !Number.isInteger(n)) {
            rowIssues.push({ rowNum, message: `Invalid Sub Qty "${sq}" (whole number >= 1)` });
          }
        }
        if (isVar) {
          const m = num(colVal(row, 'MRP'));
          if (!Number.isFinite(m) || m <= 0) rowIssues.push({ rowNum, message: 'Variant MRP must be > 0' });
          return;
        }
        if (!desc) return; // link-only or empty
        if (!sku && !previewAutoSku) rowIssues.push({ rowNum, message: 'SKU Code is required' });
        else if (seenSku.has(sku)) rowIssues.push({ rowNum, message: `Duplicate SKU Code "${sku}" (also row ${seenSku.get(sku)})` });
        else seenSku.set(sku, rowNum);
        if (!String(colVal(row, 'HSN Code') ?? '').trim()) rowIssues.push({ rowNum, message: 'HSN Code is required' });
        const g = num(colVal(row, 'GST Rate'));
        if (!Number.isFinite(g) || g < 0 || g > 100) rowIssues.push({ rowNum, message: 'Invalid GST Rate (0-100)' });
        const m = num(colVal(row, 'MRP'));
        if (!Number.isFinite(m) || m <= 0) rowIssues.push({ rowNum, message: 'Invalid MRP (must be > 0)' });
        offerHeadersForValidation.forEach(k => {
          const v = colVal(row, k);
          if (v === undefined || v === null || v === '') return;
          const n = num(v);
          if (!Number.isFinite(n) || n < 0) rowIssues.push({ rowNum, message: `Invalid ${k} price "${v}"` });
        });
      });
      
      // Preview first 5 rows
      const previewRows = rows.slice(0, 5).map((row, idx) => {
        const rowData = {};
        headers.forEach((header, colIdx) => {
          rowData[header] = row[colIdx];
        });
        return { rowNum: idx + 2, data: rowData };
      });

      // Grouped preview: each Item row with its Sub-item / Variant rows nested.
      const rowToObj = (row) => {
        const o = {};
        headers.forEach((h, ci) => { o[h] = row[ci]; });
        return o;
      };
      const rowTypeIdx = headers.findIndex(h => h.toLowerCase() === 'row type');
      const groups = [];
      let currentGroup = null;
      rows.slice(0, 100).forEach((row, idx) => {
        const type = rowTypeIdx >= 0 ? String(row[rowTypeIdx] || '').trim().toLowerCase() : '';
        const isSub = type.startsWith('sub') || type.startsWith('add');
        const isVar = type.startsWith('var');
        const node = { rowNum: idx + 2, kind: isSub ? 'sub' : isVar ? 'variant' : 'item', data: rowToObj(row) };
        if (node.kind === 'item' || !currentGroup) {
          currentGroup = { ...node, children: [] };
          groups.push(currentGroup);
        } else {
          currentGroup.children.push(node);
        }
      });
      
      setPreviewData({
        drawingNotice,
        headers,
        totalRows: rows.length,
        previewRows,
        groups,
        missingHeaders,
        missingAdditionalHeaders,
        extraHeaders,
        requiredBaseHeaders,
        offerTypeHeadersPresent: offerTypeHeaders.filter(h => headers.includes(h)),
        additionalFieldsExpected: brandAdditionalFields,
        rowIssues
      });
      setShowPreview(true);
      setPreviewChecked(true);
      
      toast.success(`Preview Ready - ${rows.length} data rows, ${headers.length} columns`);
      
    } catch (error) {
      console.error('Preview error:', error);
      toast.error(error.message || 'Failed to read Excel file.');
      setPreviewChecked(false);
    }
  };

  // Public URL QR value: `${companyPublicUrl}/items/{id}` when configured,
  // otherwise fall back to the raw item id (legacy behaviour).
  const buildQrValue = (item) => {
    if (!item?._id) return '';
    const base = (companyPublicUrl || '').trim().replace(/\/+$/, '');
    return base ? `${base}/items/${item._id}` : item._id;
  };

  // Function to generate QR code for item
  const generateQRCode = async (item) => {
    setQrItem(item);
    setShowQRModal(true);
    try {
      const qrData = buildQrValue(item);
      const dataUrl = await QRCode.toDataURL(qrData, {
        width: 256,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#ffffff'
        }
      });
      setQrCodeDataUrl(dataUrl);
    } catch (error) {
      console.error('QR Code generation error:', error);
      toast.error('Failed to generate QR code');
    }
  };

  // Handle PNG download of the generated QR code
  const handleDownloadQR = () => {
    if (!qrCodeDataUrl) return;
    const link = document.createElement('a');
    link.href = qrCodeDataUrl;
    link.download = `item-${qrItem?._id || ''}-qr.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Convert the hidden QR SVG into a PNG data URL for the PDF label
  const downloadQRPdf = async (includeQrName = false) => {
    const svg = document.getElementById('item-qr-code-svg');
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      const pngDataUrl = canvas.toDataURL('image/png');

      if (includeQrName) {
        downloadQRWithNamePdf(pngDataUrl);
      } else {
        downloadQROnlyPdf(pngDataUrl);
      }
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
  };

  const downloadQROnlyPdf = (qrImageData) => {
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: [45, 65]
    });
    pdf.addImage(qrImageData, 'PNG', 5, 15, 35, 35);
    const itemName = qrItem?.skuDescription || qrItem?.skuCode || 'Item';
    pdf.save(`${itemName}_QR.pdf`);
  };

  const downloadQRWithNamePdf = (qrImageData) => {
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: [45, 65]
    });

    const qrNameText = qrName || 'DOMUS';
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    const qrNameWidth = pdf.getTextWidth(qrNameText);
    const qrNameX = (45 - qrNameWidth) / 2;
    const qrNameY = 8;
    pdf.text(qrNameText, qrNameX, qrNameY);

    const qrSize = 25;
    const qrX = (45 - qrSize) / 2;
    const qrY = qrNameY + 4;
    pdf.addImage(qrImageData, 'PNG', qrX, qrY, qrSize, qrSize);

    const arrowY = qrY + qrSize + 4;
    const arrowX = 45 / 2;
    pdf.setLineWidth(0.5);
    pdf.line(arrowX, arrowY, arrowX, arrowY + 3);
    pdf.line(arrowX - 1, arrowY + 2, arrowX, arrowY + 3);
    pdf.line(arrowX + 1, arrowY + 2, arrowX, arrowY + 3);

    const itemNameText = qrItem?.skuDescription || qrItem?.skuCode || 'Item';
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'bold');
    const maxWidth = 38;
    const lines = pdf.splitTextToSize(itemNameText, maxWidth);
    const lineHeight = 3.0;
    const startY = arrowY + 8;

    lines.forEach((line, index) => {
      const lineWidth = pdf.getTextWidth(line);
      const lineX = (45 - lineWidth) / 2;
      const lineY = startY + (index * lineHeight);
      pdf.text(line, lineX, lineY);
    });

    pdf.save(`${itemNameText}_QR_with_name.pdf`);
  };

  // Fetch company QR name (used for the "PDF + Name" QR label)
  useEffect(() => {
    const fetchCompanyQrName = async () => {
      try {
        const companyId = user?.user?.company?._id || user?.user?.company;
        if (companyId) {
          const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/companies/${companyId}`);
          setQrName(res.data.qrName);
          setCompanyPublicUrl(res.data.publicUrl || '');
        }
      } catch (error) {
        console.error('Error fetching company QR name:', error);
      }
    };
    fetchCompanyQrName();
  }, []);

  const handleImportSubmit = async () => {
    if (!importBrand || !importFile || !importCategory) {
      toast.warning('Please select a brand and category before importing.');
      return;
    }
    
    if (!previewChecked) {
      toast.warning('Please check the Excel file using "Check Excel" button before importing.');
      return;
    }
    
    setImporting(true);
    setImportResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('brand', importBrand.value);
      formData.append('categoryId', importCategory.value);
      formData.append('series', importSeries?.value || 'RS');
      formData.append('applyGstToOffers', applyGstToOffers ? 'true' : 'false');
      formData.append('strict', strictImport ? 'true' : 'false');
      if (applyGstToOffers) {
        formData.append('gstOfferTypes', JSON.stringify(gstOfferTypes));
      }
      
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/items/import-excel`,
        formData,
        {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      if (response.data.success) {
        setImportResult({
          success: true,
          message: response.data.message,
          createdCount: response.data.createdCount,
          errorCount: response.data.errorCount,
          subItemUpdatedCount: response.data.subItemUpdatedCount || 0,
          variantUpdatedCount: response.data.variantUpdatedCount || 0,
          imageAttachedCount: response.data.imageAttachedCount || 0,
          errors: response.data.errors,
          warnings: response.data.warnings || []
        });
        // Refresh items list
        fetchItems();
      } else {
        setImportResult({
          success: false,
          message: response.data.message || 'Import failed',
          errorCount: 1,
          errors: [{ message: response.data.message }]
        });
      }
    } catch (err) {
      const data = err.response?.data || {};
      const msg = data.message || 'Import failed. Check your connection.';
      const serverErrors = Array.isArray(data.errors) ? data.errors : [];
      setImportResult({
        success: false,
        message: msg,
        errorCount: data.errorCount || serverErrors.length || 1,
        errors: serverErrors.length ? serverErrors : [{ message: msg }]
      });
    } finally {
      setImporting(false);
    }
  };

  const handleFilterChange = (e) => {
    const value = e.target.value;
    setFilter(value);
  };

  // Brand filter for the item list
  const handleListBrandChange = (selectedOption) => {
    setListBrand(selectedOption || null);
    setPage(0);
  };

  const resetForm = () => {
    setForm({
      brand: '',
      category: '',
      skuCode: '',
      skuDescription: '',
      series: 'RS',
      hsnCode: '',
      gstRate: '',
      mrp: '',
      offerType: {},
      image: null,
      additionalFieldValues: {},
      subItems: [],
      variants: [],
      isActive: true
    });
    setImagePreview(null);
    setEditingOtherImageIds([]);
    setOtherImageFiles([]);
    setOtherImageRemoved([]);
    setPrimaryOtherImageId(null);
    setSelectedBrand(null);
    setSelectedCategory(null);
    setBrandAdditionalFields([]);
    setSubItemToAdd(null);
    setSubItemQtyToAdd(1);
    setIsEditing(false);
    setEditingId(null);
  };

  const handleEdit = async (item) => {
    // Delete any existing draft when editing an item
    if (draftManager.currentDraftId) {
      try {
        await draftManager.deleteDraft(draftManager.currentDraftId);
        console.log('✅ Draft deleted when editing item');
      } catch (draftError) {
        console.error('⚠️ Failed to delete draft on edit:', draftError);
        // Continue anyway, draft deletion is not critical
      }
    }

    const convertUrlToFile = async (url) => {
      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Failed to fetch image: ${response.status}`);
        }
        const data = await response.blob();
        return new File([data], `image.${data.type.split('/')[1]}`, { type: data.type });
      } catch (error) {
        console.error('Error converting URL to file:', error);
        // Return a placeholder or null if image fetch fails
        return null;
      }
    };
    
    setIsEditing(true);
    setEditingId(item._id);

    const imageUrl = `${process.env.REACT_APP_API_BASE_URL}/items/image/${item._id}`;

    // Always show the previous image immediately while editing
    setImagePreview(imageUrl);
    setEditingOtherImageIds((item.otherImageIds || []).map(String));
    setOtherImageFiles([]);
    setOtherImageRemoved([]);
    setPrimaryOtherImageId(null);

    // Try to fetch the image as a File so it survives submission unchanged
    const imageFile = await convertUrlToFile(imageUrl);

    // Validate image file if it was successfully fetched
    if (imageFile && imageFile.type && !imageFile.type.startsWith('image/')) {
      Swal.fire('Error', 'Only image files are allowed!', 'error');
      return;
    }

    // Set the selected brand and category for react-select
    const selectedBrandOption = brands.find(brand => brand.value === item.brand._id);
    setSelectedBrand(selectedBrandOption);
    
    // Load brand additional fields
    const brand = brands.find(b => b.value === item.brand._id);
    setBrandAdditionalFields(brand?.additionalFields || []);
    
    const selectedCategoryOption = selectedBrandOption?.categories.find(cat => cat._id === item.category._id);
    const categorySelectOption = selectedCategoryOption ? {
      value: selectedCategoryOption._id,
      label: selectedCategoryOption.name
    } : null;
    setSelectedCategory(categorySelectOption);

    setForm({
      brand: item.brand._id,
      category: item.category._id,
      skuCode: item.skuCode,
      skuDescription: item.skuDescription,
      series: item.series || 'RS',
      hsnCode: item.hsnCode,
      gstRate: item.gstRate,
      mrp: item.mrp,
      offerType: item.offerType,
      image: imageFile || null, // Handle null case if image fetch failed
      additionalFieldValues: item.additionalFieldValues || {},
      isActive: item.isActive !== false,
      subItems: (item.subItems || []).map(s => ({
        item: s.item && typeof s.item === 'object'
          ? {
              _id: s.item._id,
              skuCode: s.item.skuCode,
              skuDescription: s.item.skuDescription,
              variants: (s.item.variants || []).map(v => v.name).filter(Boolean)
            }
          : { _id: s.item, skuCode: '', skuDescription: '', variants: [] },
        quantity: s.quantity || 1,
        variantName: s.variantName || ''
      })),
      variants: (item.variants || []).map(v => ({
        variantId: v.variantId || '',
        name: v.name || '',
        additionalFieldValues: v.additionalFieldValues || {},
        mrp: v.mrp ?? '',
        offerType: v.offerType || {},
        imageId: v.imageId || null,
        otherImageIds: (v.otherImageIds || []).map(String),
        imageFile: null,
        imagePreview: null,
        preferredSubItems: (v.preferredSubItems || []).map(String),
        isDefault: !!v.isDefault,
        isActive: v.isActive !== false
      }))
    });
    
    // Keep the preview visible even if the File conversion failed
    if (!imageFile) {
      console.warn('⚠️ Failed to load item image as file, you may need to re-upload');
    }
    
    // Focus on SKU code field after a short delay to ensure the form is rendered
    setTimeout(() => {
      const skuCodeInput = document.getElementById('skuCode');
      if (skuCodeInput) {
        skuCodeInput.focus();
        skuCodeInput.scrollIntoView({ behavior: 'smooth' });
        skuCodeInput.select();
      }
    }, 300); // Increased timeout from 100ms to 300ms for better react-select option loading
  };

  // Load latest item with populated audit trail and open the modal
  const showItemDetails = async (item) => {
    setSimilarItems([]);
    const base = process.env.REACT_APP_API_BASE_URL;
    try {
      // Prefer stable _id based fetch to avoid SKU mismatches
      const { data } = await axios.get(`${base}/items/id/${encodeURIComponent(item._id)}`);
      setModalItem(data);
      setViewImageIndex(0);
      setModalOpen(true);
      try {
        const sim = await axios.get(`${base}/items/id/${encodeURIComponent(item._id)}/similar`);
        setSimilarItems(sim.data?.similarItems || []);
      } catch (_) {
        setSimilarItems([]);
      }
    } catch (err) {
      // Fallback: try by skuCode once
      try {
        const { data } = await axios.get(`${base}/items/${encodeURIComponent(item.skuCode)}`);
        setModalItem(data);
      } catch (_) {
        setModalItem(item);
      }
      setModalOpen(true);
    }
  };

  // Get available cameras for the scanner
  const getAvailableCameras = async () => {
    try {
      const cameras = await QrScanner.listCameras(true);
      console.log('Available cameras:', cameras);
      setAvailableCameras(cameras);

      if (!selectedCameraId && cameras.length > 0) {
        const rearCamera = cameras.find(camera =>
          camera.label.toLowerCase().includes('back') ||
          camera.label.toLowerCase().includes('rear') ||
          camera.label.toLowerCase().includes('environment')
        );
        setSelectedCameraId(rearCamera ? rearCamera.id : cameras[0].id);
      }
    } catch (error) {
      console.error('Error getting cameras:', error);
      Swal.fire({
        icon: 'error',
        title: 'Camera Error',
        text: 'Could not access camera devices. Please ensure camera permissions are granted.',
      });
    }
  };

  const openQRScanner = async () => {
    await getAvailableCameras();
    setShowQRScanner(true);
    setTimeout(() => startQRScanner(), 100);
  };

  const startQRScanner = async () => {
    try {
      setQrScannerLoading(true);

      if (availableCameras.length === 0) {
        await getAvailableCameras();
      }

      setQrScannerLoading(false);
    } catch (error) {
      console.error('Error preparing QR scanner:', error);
      setQrScannerLoading(false);

      let errorMessage = 'Could not start QR scanner.';
      if (error.message?.includes('Permission denied') || error.name === 'NotAllowedError') {
        errorMessage = 'Camera access was denied. Please allow camera permissions and try again.';
      } else if (error.message?.includes('NotFoundError') || error.name === 'NotFoundError') {
        errorMessage = 'No cameras found on this device.';
      } else if (error.message?.includes('OverconstrainedError') || error.name === 'OverconstrainedError') {
        errorMessage = 'The selected camera is not available. Please try a different camera.';
        if (availableCameras.length > 0) {
          setSelectedCameraId(availableCameras[0].id);
        }
      }

      Swal.fire({
        icon: 'error',
        title: 'Camera Error',
        text: errorMessage,
      });
    }
  };

  const stopQRScanner = async () => {
    setShowQRScanner(false);
    setQrScannerLoading(false);
  };

  const handleQRScanSuccess = async (qrCodeData) => {
    if (qrCodeData) {
      try {
        stopQRScanner();

        const scanned = String(qrCodeData).trim();
        console.log('QR Code scanned:', scanned);

        // Accept either a public URL (…/items/<id>) or a raw item ObjectId.
        const urlMatch = scanned.match(/\/items\/([0-9a-fA-F]{24})(?:[/?#]|$)/);
        const itemId = urlMatch ? urlMatch[1] : scanned;

        if (!/^[0-9a-fA-F]{24}$/.test(itemId)) {
          console.warn('QR code does not contain a valid ObjectId:', scanned);
          throw new Error('Invalid QR code format. Expected a valid item ID.');
        }

        await showItemDetails({ _id: itemId });

        Swal.fire({
          icon: 'success',
          title: 'QR Code Scanned!',
          text: 'Item found successfully',
          timer: 2000,
          showConfirmButton: false
        });
      } catch (error) {
        console.error('Error finding scanned item:', error);

        let errorMessage = 'Item not found';
        if (error.message?.includes('Invalid QR code format')) {
          errorMessage = 'Invalid QR code format';
        } else if (error.response?.status === 404) {
          errorMessage = 'Item not found';
        } else if (error.response?.status === 401) {
          errorMessage = 'Authentication failed. Please login again.';
        } else if (error.response?.status >= 500) {
          errorMessage = 'Server error. Please try again later.';
        }

        Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: errorMessage,
          timer: 2000,
          showConfirmButton: false
        });
      }
    }
  };

  const handleQRError = (error) => {
    console.error('QR Scanner error:', error);
    setQrScannerLoading(false);
    Swal.fire({
      icon: 'error',
      title: 'Camera Error',
      text: typeof error === 'string' ? error : 'Failed to access camera. Please check your permissions.',
    });
  };

  const handleQRScan = (data) => {
    if (data) {
      console.log('QR Code detected:', data);
      handleQRScanSuccess(data);
    }
  };

  const handleCameraLoad = (isLoading) => {
    setQrScannerLoading(isLoading);
  };

  // Show UpdatedBy history modal
  const showUpdateHistory = async (item) => {
    console.log('🔍 showUpdateHistory called with item:', {
      id: item._id,
      skuCode: item.skuCode,
      hasUpdatedBy: !!item.updatedBy,
      updatedByLength: item.updatedBy?.length
    });
    
    try {
      const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/items/id/${encodeURIComponent(item._id)}`;
      console.log('📡 Making API call to:', apiUrl);
      console.log('🌍 API Base URL:', process.env.REACT_APP_API_BASE_URL);
      
      const { data } = await axios.get(apiUrl);
      
      console.log('✅ API response received:', {
        id: data._id,
        skuCode: data.skuCode,
        hasUpdatedBy: !!data.updatedBy,
        updatedByLength: data.updatedBy?.length
      });
      
      setUpdatedByModal({
        isOpen: true,
        item: data
      });
    } catch (error) {
      console.error('❌ Error fetching item history:', error);
      console.error('❌ Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      Swal.fire('Error', `Failed to load update history: ${error.message}`, 'error');
    }
  };

  // Custom styles for react-select with dark mode support
  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: '42px',
      backgroundColor: darkMode ? '#374151' : '#ffffff',
      borderColor: darkMode ? '#4B5563' : (state.isFocused ? '#6366F1' : '#D1D5DB'),
      boxShadow: state.isFocused ? (darkMode ? '0 0 0 1px #6366F1' : '0 0 0 1px #6366F1') : 'none',
      '&:hover': {
        borderColor: darkMode ? '#6B7280' : '#9CA3AF'
      },
      color: darkMode ? '#F3F4F6' : '#111827'
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: darkMode ? '#374151' : '#ffffff',
      border: darkMode ? '1px solid #4B5563' : '1px solid #D1D5DB'
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected 
        ? (darkMode ? '#4F46E5' : '#6366F1')
        : state.isFocused 
          ? (darkMode ? '#4B5563' : '#F3F4F6')
          : (darkMode ? '#374151' : '#ffffff'),
      color: state.isSelected ? '#ffffff' : (darkMode ? '#F3F4F6' : '#111827'),
      '&:hover': {
        backgroundColor: state.isSelected 
          ? (darkMode ? '#4F46E5' : '#6366F1')
          : (darkMode ? '#4B5563' : '#E5E7EB')
      }
    }),
    singleValue: (base) => ({
      ...base,
      color: darkMode ? '#F3F4F6' : '#111827'
    }),
    input: (base) => ({
      ...base,
      color: darkMode ? '#F3F4F6' : '#111827'
    }),
    placeholder: (base) => ({
      ...base,
      color: darkMode ? '#9CA3AF' : '#6B7280'
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: darkMode ? '#4B5563' : '#E5E7EB'
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: darkMode ? '#F3F4F6' : '#111827'
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: darkMode ? '#9CA3AF' : '#6B7280',
      '&:hover': {
        backgroundColor: darkMode ? '#EF4444' : '#FEE2E2',
        color: darkMode ? '#ffffff' : '#DC2626'
      }
    })
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Enhanced Toast Notification with Dark Mode */}
      {showNotification && (
        <div 
          className={`fixed top-4 right-4 z-50 border-l-4 border-green-500 rounded-lg shadow-2xl p-4 flex items-center space-x-3 animate-fade-in transform transition-all duration-300 ${
            darkMode 
              ? 'bg-gray-800 border-green-400' 
              : 'bg-white border-green-500'
          }`}
          style={{ 
            minWidth: '320px',
            animation: 'slideInFromRight 0.4s ease-out'
          }}
        >
          <div className={`flex-shrink-0 p-1 rounded-full ${
            darkMode ? 'bg-green-900/30' : 'bg-green-50'
          }`}>
            <svg className={`h-5 w-5 ${
              darkMode ? 'text-green-400' : 'text-green-500'
            }`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div className="flex-1">
            <p className={`text-sm font-medium transition-colors duration-300 ${
              darkMode ? 'text-gray-100' : 'text-gray-900'
            }`}>
              {notificationMessage}
            </p>
          </div>
          <button
            onClick={() => setShowNotification(false)}
            className={`flex-shrink-0 p-1 rounded-full transition-all duration-200 hover:scale-110 ${
              darkMode 
                ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' 
                : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
            }`}
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      <div className={`p-6 shadow-2xl rounded-2xl transition-all duration-300 ${
        darkMode 
          ? 'bg-gradient-to-br from-gray-800 via-gray-800 to-gray-900 border border-gray-700' 
          : 'bg-gradient-to-br from-white via-white to-gray-50 border border-gray-100'
      }`}>
        {/* Header */}
        <div className="mb-4 sm:mb-6 md:mb-8">
          <div className={`flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 sm:p-6 rounded-xl transition-colors duration-300 ${
            darkMode 
              ? 'bg-gradient-to-r from-indigo-900 via-purple-900 to-pink-900' 
              : 'bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600'
          }`}>
            <div className="flex items-center space-x-3 sm:space-x-4 mb-3 sm:mb-0">
              <div className="bg-white bg-opacity-20 backdrop-blur-sm p-2 sm:p-3 rounded-lg">
                <svg className="w-6 h-6 sm:w-8 sm:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              </div>
              <div>
                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white drop-shadow-lg">
                  Item Management
                </h2>
                <p className="text-indigo-100 text-xs sm:text-sm mt-1">Manage your product inventory</p>
              </div>
            </div>
            <div className="flex items-center space-x-2 bg-white bg-opacity-20 backdrop-blur-sm px-3 sm:px-4 py-2 rounded-lg">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span className="text-white font-medium text-sm sm:text-base">{isEditing ? 'Edit Mode' : 'Create Mode'}</span>
            </div>
            <button
              type="button"
              onClick={openQRScanner}
              className="flex items-center space-x-2 bg-indigo-600 hover:bg-indigo-700 text-white px-3 sm:px-4 py-2 rounded-lg font-medium text-sm sm:text-base transition-colors duration-200"
            >
              <FaQrcode className="w-4 h-4 sm:w-5 sm:h-5" />
              <span>Scan QR Code</span>
            </button>
            <button
              type="button"
              onClick={() => { setShowImport(true); setImportResult(null); }}
              className="flex items-center space-x-2 bg-emerald-500 hover:bg-emerald-600 text-white px-3 sm:px-4 py-2 rounded-lg font-medium text-sm sm:text-base transition-colors duration-200"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <span>Import Excel</span>
            </button>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
        {/* Form Fields Card */}
        <div className={`p-4 sm:p-6 rounded-xl border transition-all duration-300 ${
          darkMode 
            ? 'bg-gradient-to-br from-gray-800 via-gray-800 to-gray-900 border-gray-600 shadow-2xl' 
            : 'bg-gradient-to-br from-white via-white to-gray-50 border-gray-200 shadow-xl'
        }`}>
          <div className={`mb-4 sm:mb-6 pb-4 border-b ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
            <h3 className={`text-lg sm:text-xl font-bold flex items-center transition-colors duration-300 ${
              darkMode ? 'text-white' : 'text-gray-900'
            }`}>
              <div className={`p-2 rounded-lg mr-3 ${darkMode ? 'bg-indigo-600' : 'bg-indigo-100'}`}>
                <svg className={`w-4 h-4 sm:w-5 sm:h-5 ${darkMode ? 'text-white' : 'text-indigo-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              Product Information
            </h3>
            <p className={`text-xs sm:text-sm mt-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Enter the basic details for your product item
            </p>
          </div>

          {/* All form fields in a responsive grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Brand */}
            <div className="space-y-2">
              <label className={`block font-semibold text-sm transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                Brand <span className="text-red-500">*</span>
              </label>
              <Select
                value={selectedBrand}
                onChange={handleBrandChange}
                options={brands}
                isLoading={loading}
                isClearable
                isSearchable
                placeholder="Select a brand..."
                className="basic-single"
                id='brand'
                classNamePrefix="select"
                noOptionsMessage={() => "No brands found"}
                loadingMessage={() => "Loading brands..."}
                styles={customSelectStyles}
              />
              {!loading && brands.length === 0 && (
                <p className="text-sm text-red-500">
                  No brands available. Please add brands first.
                </p>
              )}
            </div>

            {/* Category */}
            <div className="space-y-2">
              <label className={`block font-semibold text-sm transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                Category <span className="text-red-500">*</span>
              </label>
              <Select
                value={selectedCategory}
                onChange={handleCategoryChange}
                options={selectedBrand?.categories?.map(category => ({
                  value: category._id,
                  label: category.name
                })) || []}
                isDisabled={!selectedBrand}
                isClearable
                isSearchable
                placeholder={selectedBrand ? "Select a category..." : "Select a brand first"}
                className="basic-single"
                classNamePrefix="select"
                noOptionsMessage={() => selectedBrand ? "No categories found" : "Select a brand first"}
                styles={customSelectStyles}
              />
            </div>
          
            {/* SKU Code */}
            <div className="space-y-2">
              <label className={`block font-semibold text-sm transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                SKU Code {!autoSkuPattern && <span className="text-red-500">*</span>}
              </label>
              <input
                id="skuCode"
                type="text"
                value={form.skuCode}
                onChange={(e) => setForm({ ...form, skuCode: e.target.value })}
                className={`w-full px-3 py-2.5 border rounded-lg transition-all duration-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent hover:border-indigo-400 ${
                  darkMode 
                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:bg-gray-600 focus:shadow-lg' 
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:bg-gray-50 focus:shadow-lg'
                }`}
                placeholder={autoSkuPattern ? 'Leave blank to auto-generate' : 'Enter SKU Code'}
              />
              {autoSkuPattern && (
                <p className={`text-xs ${darkMode ? 'text-sky-300' : 'text-sky-600'}`}>
                  Auto-generated from: <code>{autoSkuPattern}</code> (leave blank)
                </p>
              )}
            </div>

            {/* SKU Description */}
            <div className="space-y-2 sm:col-span-2 lg:col-span-1">
              <label className={`block font-semibold text-sm transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                SKU Description <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.skuDescription}
                onChange={(e) => setForm({ ...form, skuDescription: e.target.value })}
                className={`w-full px-3 py-2.5 border rounded-lg transition-all duration-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                  darkMode 
                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                }`}
                placeholder="Enter SKU Description"
              />
            </div>

            {/* Series */}
            <div className="space-y-2">
              <label className={`block font-semibold text-sm transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                Series <span className="text-red-500">*</span>
              </label>
              <Select
                value={seriesOptions.find(option => option.value === form.series)}
                onChange={(selectedOption) => setForm({ ...form, series: selectedOption?.value || 'RS' })}
                options={seriesOptions}
                isSearchable={false}
                placeholder="Select Series..."
                className="basic-single"
                classNamePrefix="select"
                styles={customSelectStyles}
              />
            </div>

            {/* HSN Code */}
            <div className="space-y-2">
              <label className={`block font-semibold text-sm transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                HSN Code <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.hsnCode}
                onChange={(e) => setForm({ ...form, hsnCode: e.target.value })}
                className={`w-full px-3 py-2.5 border rounded-lg transition-all duration-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                  darkMode 
                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                }`}
                placeholder="Enter HSN Code"
              />
            </div>

            {/* GST Rate */}
            <div className="space-y-2">
              <label className={`block font-semibold text-sm transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                GST Rate (%) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={form.gstRate}
                onChange={(e) => setForm({ ...form, gstRate: e.target.value })}
                className={`w-full px-3 py-2.5 border rounded-lg transition-all duration-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                  darkMode 
                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                }`}
                placeholder="e.g., 18"
              />
            </div>

            {/* Active status */}
            <div className="space-y-2">
              <label className={`block font-semibold text-sm transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                Status
              </label>
              <button
                type="button"
                onClick={() => setForm({ ...form, isActive: !(form.isActive !== false) })}
                className={`w-full flex items-center justify-between px-3 py-2.5 border rounded-lg transition-all duration-200 ${
                  form.isActive !== false
                    ? (darkMode ? 'bg-green-900/30 border-green-700 text-green-300' : 'bg-green-50 border-green-300 text-green-700')
                    : (darkMode ? 'bg-red-900/30 border-red-700 text-red-300' : 'bg-red-50 border-red-300 text-red-700')
                }`}
                title="Toggle whether this item is active"
              >
                <span className="font-semibold text-sm">{form.isActive !== false ? 'Active' : 'Inactive'}</span>
                <span className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${form.isActive !== false ? 'bg-green-500' : 'bg-gray-400'}`}>
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${form.isActive !== false ? 'translate-x-4' : 'translate-x-0.5'}`} />
                </span>
              </button>
            </div>

            {/* Product Image Upload */}
            <div className="space-y-2 sm:col-span-2 lg:col-span-3">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <label className={`block font-semibold text-sm transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  Product Image
                </label>
                <button
                  type="button"
                  onClick={handleWebImageSearch}
                  disabled={isWebImageSearching}
                  className={`inline-flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-xs sm:text-sm font-semibold border transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-60 disabled:cursor-not-allowed ${
                    darkMode
                      ? 'bg-indigo-900/30 border-indigo-600/50 text-indigo-300 hover:bg-indigo-900/50 hover:border-indigo-500'
                      : 'bg-indigo-50 border-indigo-200 text-indigo-700 hover:bg-indigo-100 hover:border-indigo-300'
                  }`}
                  title="Search product images on the web automatically"
                >
                  {isWebImageSearching ? (
                    <>
                      <svg className="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      <span>Searching...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <span>Auto-Find on Web</span>
                    </>
                  )}
                </button>
              </div>

              <div className="flex items-center justify-center w-full">
                <label className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-all duration-300 ${
                  darkMode
                    ? 'border-indigo-600 bg-indigo-900/10 hover:bg-indigo-900/20 hover:border-indigo-500'
                    : 'border-indigo-300 bg-indigo-50 hover:bg-indigo-100 hover:border-indigo-400'
                }`}>
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <svg className={`w-6 h-6 sm:w-8 sm:h-8 mb-4 ${darkMode ? 'text-indigo-400' : 'text-indigo-500'}`} aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 16">
                      <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"/>
                    </svg>
                    <p className={`mb-2 text-xs sm:text-sm ${darkMode ? 'text-indigo-300' : 'text-indigo-600'}`}>
                      <span className="font-semibold">Click to upload</span> or drag and drop
                    </p>
                    <p className={`text-xs ${darkMode ? 'text-indigo-400' : 'text-indigo-500'}`}>
                      JPG, PNG, GIF, WebP (MAX. 5MB)
                    </p>
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        if (file.type && !file.type.startsWith('image/')) {
                          Swal.fire('Error', 'Please select a valid image file', 'error');
                          e.target.value = '';
                          return;
                        }
                        if (file.size > 5 * 1024 * 1024) {
                          Swal.fire('Error', 'Image size should be less than 5MB', 'error');
                          e.target.value = '';
                          return;
                        }
                        setForm({ ...form, image: file });
                        setImagePreview(URL.createObjectURL(file));
                        console.log('🖼️ Image selected for draft:', file.name, file.size);
                      }
                    }}
                  />
                </label>
              </div>
              {imagePreview && (
                <div className="relative mt-4">
                  <div className="relative">
                    <img 
                      src={imagePreview} 
                      alt="Item Preview" 
                      className={`w-full max-w-xs h-32 sm:h-48 object-cover rounded-lg border-2 transition-colors duration-300 ${
                        darkMode ? 'border-gray-600' : 'border-gray-300'
                      }`}
                    />
                    {/* Draft indicator for images loaded from draft */}
                    {typeof form.image === 'string' && form._imageFileName && (
                      <div className={`absolute bottom-0 left-0 right-0 px-2 py-1 rounded-b-lg text-xs ${
                        darkMode 
                          ? 'bg-indigo-900/80 text-indigo-300 backdrop-blur-sm' 
                          : 'bg-blue-900/80 text-blue-100 backdrop-blur-sm'
                      }`}>
                        📄 From Draft: {form._imageFileName}
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setImagePreview(null);
                        setForm({ ...form, image: null, _imageFileName: null, _imageFileSize: null });
                        const fileInput = document.querySelector('input[type="file"]');
                        if (fileInput) fileInput.value = '';
                      }}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center text-xs sm:text-sm hover:bg-red-600 transition-colors duration-200 shadow-lg"
                      title="Remove image"
                    >
                      <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              )}

              {/* Additional images (editable carousel) */}
              <div className={`mt-4 rounded-lg border p-3 ${darkMode ? 'border-gray-600 bg-gray-800/40' : 'border-gray-200 bg-gray-50'}`}>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <label className={`text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                    Additional Images
                  </label>
                  <label className={`text-xs px-2 py-1 rounded cursor-pointer ${darkMode ? 'bg-indigo-900/40 text-indigo-300' : 'bg-indigo-100 text-indigo-700'}`}>
                    + Add images
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        const valid = files.filter(f => f.type && f.type.startsWith('image/') && f.size <= 5 * 1024 * 1024);
                        if (valid.length !== files.length) {
                          Swal.fire('Warning', 'Some files were skipped (images under 5MB only).', 'warning');
                        }
                        setOtherImageFiles(prev => [...prev, ...valid]);
                        e.target.value = '';
                      }}
                    />
                  </label>
                </div>
                {otherImageItems.length === 0 ? (
                  <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No additional images.</p>
                ) : (
                  <div className="flex gap-2 overflow-x-auto pb-1">
                    {otherImageItems.map((item, index) => (
                      <div key={item.id || `${item.file?.name}-${index}`} className="relative shrink-0">
                        <img src={item.url} alt="Additional" className={`w-20 h-20 object-cover rounded-lg border ${primaryOtherImageId && String(primaryOtherImageId) === String(item.id) ? 'border-indigo-500 ring-2 ring-indigo-400' : 'border-gray-300'}`} />
                        {item.existing && (
                          <button
                            type="button"
                            onClick={() => { setPrimaryOtherImageId(item.id); setImagePreview(item.url); }}
                            className="absolute -top-1.5 -left-1.5 px-1.5 h-5 rounded bg-indigo-600 text-white text-[10px] flex items-center justify-center hover:bg-indigo-700"
                            title="Make primary image"
                          >
                            ★
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            if (item.existing) {
                              setOtherImageRemoved(prev => [...prev, item.id]);
                            } else {
                              const newIndex = index - keptOtherImageIds.length;
                              setOtherImageFiles(prev => prev.filter((_, i) => i !== newIndex));
                            }
                          }}
                          className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center hover:bg-red-600"
                          title="Remove image"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Pricing Section */}
          <div className="mt-4 sm:mt-6">
            <div className={`p-4 rounded-lg border transition-colors duration-300 ${
              darkMode
                ? 'bg-gradient-to-r from-emerald-900/30 to-teal-900/30 border-emerald-700/50'
                : 'bg-gradient-to-r from-emerald-50/50 to-teal-50/50 border-emerald-200/50'
            }`}>
              <h4 className={`text-sm sm:text-base font-semibold mb-4 flex items-center transition-colors duration-300 ${
                darkMode ? 'text-emerald-300' : 'text-emerald-700'
              }`}>
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
                Pricing Information
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* MRP - Required field */}
                <div className="space-y-2">
                  <label className={`block text-sm font-medium transition-colors duration-300 ${
                    darkMode ? 'text-gray-200' : 'text-gray-700'
                  }`}>
                    MRP (₹) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.mrp}
                    onChange={(e) => setForm({ ...form, mrp: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg transition-all duration-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 hover:border-emerald-400 ${
                      darkMode
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:bg-gray-600'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:bg-gray-50'
                    }`}
                    placeholder="Enter MRP"
                    required
                  />
                </div>

                {/* Dynamic offer types from the selected brand */}
                {getActiveOfferTypes(selectedBrand).map((ot) => {
                  if (ot.key === 'flat') {
                    return (
                      <div key="flat" className={`text-xs px-3 py-1.5 rounded-lg ${
                        darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'
                      }`}>
                        Flat = MRP (set automatically)
                      </div>
                    );
                  }
                  return (
                    <div className="space-y-2" key={ot.key}>
                      <label className={`block text-sm font-medium transition-colors duration-300 ${
                        darkMode ? 'text-gray-200' : 'text-gray-700'
                      }`}>
                        {ot.label} (₹)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={form.offerType[ot.key] || ''}
                        onChange={(e) =>
                          setForm({ ...form, offerType: { ...form.offerType, [ot.key]: e.target.value } })
                        }
                        className={`w-full px-3 py-2 border rounded-lg transition-all duration-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 hover:border-emerald-400 ${
                          darkMode
                            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:bg-gray-600'
                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:bg-gray-50'
                        }`}
                        placeholder={ot.label}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          </div>

          {/* Additional Fields Section */}
          {visibleAdditionalFields.length > 0 && (
            <div className="mt-4 sm:mt-6 col-span-full">
              <div className={`p-4 rounded-lg border transition-colors duration-300 ${
                darkMode
                  ? 'bg-gradient-to-r from-purple-900/30 to-pink-900/30 border-purple-700/50'
                  : 'bg-gradient-to-r from-purple-50/50 to-pink-50/50 border-purple-200/50'
              }`}>
                <h4 className={`text-sm sm:text-base font-semibold mb-4 flex items-center transition-colors duration-300 ${
                  darkMode ? 'text-purple-300' : 'text-purple-700'
                }`}>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17v4a2 2 0 002 2h4M13 13h3a2 2 0 012 2v1" />
                  </svg>
                  Additional Brand Fields
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {visibleAdditionalFields.map((field, index) => (
                    <div key={index} className="space-y-2">
                      <label className={`block text-sm font-medium transition-colors duration-300 ${
                        darkMode ? 'text-gray-200' : 'text-gray-700'
                      }`}>
                        {field.fieldName}
                        {field.isRequired && <span className="text-red-500 ml-1">*</span>}
                      </label>
                      {field.fieldType === 'number' ? (
                        <input
                          type="number"
                          min={field.min ?? undefined}
                          max={field.max ?? undefined}
                          step={field.decimals ? 1 / Math.pow(10, field.decimals) : undefined}
                          value={form.additionalFieldValues[field.fieldName] || ''}
                          onChange={(e) => handleAdditionalFieldValueChange(field.fieldName, e.target.value)}
                          className={`w-full px-3 py-2 border rounded-lg transition-all duration-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                            darkMode
                              ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                              : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                          }`}
                          placeholder={`Enter ${field.fieldName}`}
                          required={field.isRequired}
                        />
                      ) : field.fieldType === 'select' ? (
                        <select
                          value={form.additionalFieldValues[field.fieldName] || ''}
                          onChange={(e) => handleAdditionalFieldValueChange(field.fieldName, e.target.value)}
                          className={`w-full px-3 py-2 border rounded-lg transition-all duration-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                            darkMode
                              ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                              : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                          }`}
                          required={field.isRequired}
                        >
                          <option value="">Select {field.fieldName}</option>
                          {(field.options || []).map((opt, i) => (
                            <option key={i} value={opt}>{opt}</option>
                          ))}
                        </select>
                      ) : field.fieldType === 'date' ? (
                        <input
                          type="date"
                          value={form.additionalFieldValues[field.fieldName] || ''}
                          onChange={(e) => handleAdditionalFieldValueChange(field.fieldName, e.target.value)}
                          className={`w-full px-3 py-2 border rounded-lg transition-all duration-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                            darkMode
                              ? 'bg-gray-700 border-gray-600 text-white'
                              : 'bg-white border-gray-300 text-gray-900'
                          }`}
                          required={field.isRequired}
                        />
                      ) : (
                        <input
                          type="text"
                          maxLength={field.maxLength || undefined}
                          value={form.additionalFieldValues[field.fieldName] || ''}
                          onChange={(e) => handleAdditionalFieldValueChange(field.fieldName, e.target.value)}
                          className={`w-full px-3 py-2 border rounded-lg transition-all duration-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                            darkMode
                              ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                              : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                          }`}
                          placeholder={`Enter ${field.fieldName}`}
                          required={field.isRequired}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Accessory classification (only when Product Type = Accessory) */}
          {selectedBrand?.accessoriesEnabled && isAccessoryContext && visibleAccessoryFields.length > 0 && (
            <div className="mt-4 sm:mt-6 col-span-full">
              <div className={`p-4 rounded-lg border transition-colors duration-300 ${
                darkMode
                  ? 'bg-gradient-to-r from-teal-900/20 to-emerald-900/20 border-teal-700/50'
                  : 'bg-gradient-to-r from-teal-50/50 to-emerald-50/50 border-teal-200/50'
              }`}>
                <h4 className={`text-sm sm:text-base font-semibold mb-1 ${darkMode ? 'text-teal-300' : 'text-teal-700'}`}>
                  Accessory classification
                </h4>
                <p className={`text-xs mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Stored on the accessory master and shared by every parent that links this item.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {visibleAccessoryFields.map((field, index) => (
                    <div key={index} className="space-y-2">
                      <label className={`block text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                        {field.fieldName}
                        {field.isRequired && <span className="text-red-500 ml-1">*</span>}
                      </label>
                      {field.fieldType === 'select' ? (
                        <select
                          value={form.additionalFieldValues[field.fieldName] || ''}
                          onChange={(e) => handleAdditionalFieldValueChange(field.fieldName, e.target.value)}
                          className={`w-full px-3 py-2 border rounded-lg ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                        >
                          <option value="">Select {field.fieldName}</option>
                          {(field.options || []).map((opt, i) => (<option key={i} value={opt}>{opt}</option>))}
                        </select>
                      ) : field.fieldType === 'date' ? (
                        <input
                          type="date"
                          value={form.additionalFieldValues[field.fieldName] || ''}
                          onChange={(e) => handleAdditionalFieldValueChange(field.fieldName, e.target.value)}
                          className={`w-full px-3 py-2 border rounded-lg ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                        />
                      ) : (
                        <input
                          type={field.fieldType === 'number' ? 'number' : 'text'}
                          value={form.additionalFieldValues[field.fieldName] || ''}
                          onChange={(e) => handleAdditionalFieldValueChange(field.fieldName, e.target.value)}
                          className={`w-full px-3 py-2 border rounded-lg ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                          placeholder={`Enter ${field.fieldName}`}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Sub-items / Add-ons Section (optional) */}
          {(selectedBrand?.accessoriesEnabled || (form.subItems || []).length > 0) && (
          <div className="mt-4 sm:mt-6 col-span-full">
            <div className={`p-4 rounded-lg border transition-colors duration-300 ${
              darkMode
                ? 'bg-gradient-to-r from-amber-900/20 to-orange-900/20 border-amber-700/50'
                : 'bg-gradient-to-r from-amber-50/50 to-orange-50/50 border-amber-200/50'
            }`}>
              <h4 className={`text-sm sm:text-base font-semibold mb-1 flex items-center transition-colors duration-300 ${
                darkMode ? 'text-amber-300' : 'text-amber-700'
              }`}>
                Sub-items / Add-ons
                <span className="ml-2 text-xs font-normal opacity-70">(optional)</span>
              </h4>
              <p className={`text-xs mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Bundle other items with this one, e.g. a Sofa with 2 Pillows. If the referenced item has variants, pin one here. Sub-items can also be set in bulk via the Excel import using Row Type "Sub-item".
              </p>

              <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
                <div className="flex-1 space-y-2">
                  <label className={`block text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Search item</label>
                  <Select
                    value={subItemToAdd}
                    options={subItemOptions}
                    onInputChange={(val, meta) => { if (meta.action === 'input-change') searchSubItemOptions(val); }}
                    onMenuOpen={() => { if (subItemOptions.length === 0) searchSubItemOptions(''); }}
                    onChange={(opt) => setSubItemToAdd(opt || null)}
                    placeholder="Search by SKU or description..."
                    className="basic-single"
                    classNamePrefix="select"
                    styles={customSelectStyles}
                    isLoading={subItemLoading}
                    isClearable
                    isSearchable
                    filterOption={null}
                  />
                </div>
                <div className="w-full sm:w-28 space-y-2">
                  <label className={`block text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>Qty</label>
                  <input
                    type="number"
                    min="1"
                    value={subItemQtyToAdd}
                    onChange={(e) => setSubItemQtyToAdd(e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg transition-all duration-200 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 ${
                      darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  />
                </div>
                <button
                  type="button"
                  onClick={handleAddSubItem}
                  disabled={!subItemToAdd}
                  className={`px-4 py-2 rounded-lg font-semibold text-white transition-colors ${
                    !subItemToAdd ? 'bg-gray-400 cursor-not-allowed' : 'bg-amber-600 hover:bg-amber-700'
                  }`}
                >
                  Add
                </button>
              </div>

              {(form.subItems || []).length > 0 && (
                <ul className="mt-4 space-y-2">
                  {form.subItems.map((s, index) => (
                    <li key={index} className={`flex items-center gap-3 px-3 py-2 rounded-lg border ${
                      darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-white border-gray-200'
                    }`}>
                      <span className={`flex-1 text-sm ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                        {s.item?.skuDescription || s.item?.skuCode || (typeof s.item === 'string' ? s.item : 'Sub-item')}
                        {s.item?.skuCode && s.item?.skuDescription ? <span className="opacity-60"> ({s.item.skuCode})</span> : null}
                      </span>
                      {(s.item?.variants || []).length > 0 && (
                        <select
                          value={s.variantName || ''}
                          onChange={(e) => handleSubItemVariantChange(index, e.target.value)}
                          className={`px-2 py-1 border rounded-lg text-sm ${
                            darkMode ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                          }`}
                          title="Pin a variant of this sub-item"
                        >
                          <option value="">Variant: Any</option>
                          {(s.item.variants || []).map((v, i) => (
                            <option key={i} value={v}>{v}</option>
                          ))}
                        </select>
                      )}
                      <input
                        type="number"
                        min="1"
                        value={s.quantity}
                        onChange={(e) => handleSubItemQtyChange(index, e.target.value)}
                        className={`w-20 px-2 py-1 border rounded-lg text-sm ${
                          darkMode ? 'bg-gray-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                        }`}
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveSubItem(index)}
                        className="text-red-500 hover:text-red-700 text-sm font-semibold"
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          )}

          {/* Variants Section (optional price list) */}
          {(selectedBrand?.advancedItems || (form.variants || []).length > 0) && (
          <div className="mt-4 sm:mt-6 col-span-full">
            <div className={`p-4 rounded-lg border transition-colors duration-300 ${
              darkMode
                ? 'bg-gradient-to-r from-violet-900/20 to-indigo-900/20 border-violet-700/50'
                : 'bg-gradient-to-r from-violet-50/50 to-indigo-50/50 border-violet-200/50'
            }`}>
              <div className="flex items-center justify-between mb-1">
                <h4 className={`text-sm sm:text-base font-semibold flex items-center ${darkMode ? 'text-violet-300' : 'text-violet-700'}`}>
                  Variants
                  <span className="ml-2 text-xs font-normal opacity-70">(optional price list)</span>
                </h4>
                <button
                  type="button"
                  onClick={handleAddVariant}
                  className="px-3 py-1.5 bg-violet-600 text-white rounded-lg hover:bg-violet-700 text-sm font-medium"
                >
                  + Add Variant
                </button>
              </div>
              <p className={`text-xs mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Each variant carries its own attributes and prices. Assign preferred sub-items (from the bundle section above) per variant.
              </p>

              {(form.variants || []).length === 0 ? (
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No variants added yet.</p>
              ) : (
                <div className="space-y-3">
                  {(form.variants || []).map((variant, index) => (
                    <div key={index} className={`p-3 rounded-lg border ${darkMode ? 'bg-gray-800/60 border-gray-600' : 'bg-white border-gray-200'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className={`text-xs font-mono ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {variant.variantId || 'New variant'}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoveVariant(index)}
                          className="text-red-500 hover:text-red-700 text-sm font-semibold"
                        >
                          Remove
                        </button>
                      </div>
                      <div className="flex items-center gap-3 mb-3">
                        {(variant.imagePreview || variant.imageId || imagePreview) && (
                          <img
                            src={variant.imagePreview || (variant.imageId ? `${apiBase}/items/image-by-id/${variant.imageId}` : imagePreview)}
                            alt={variant.name || 'Variant'}
                            className="w-14 h-14 object-cover rounded-lg border border-gray-300"
                          />
                        )}
                        <label className={`text-xs px-2 py-1 rounded cursor-pointer ${darkMode ? 'bg-violet-900/40 text-violet-300' : 'bg-violet-100 text-violet-700'}`}>
                          {variant.imageFile ? 'Change image' : 'Upload image'}
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files && e.target.files[0];
                              if (!file) return;
                              if (!file.type || !file.type.startsWith('image/') || file.size > 5 * 1024 * 1024) {
                                Swal.fire('Warning', 'Choose an image under 5MB.', 'warning');
                                e.target.value = '';
                                return;
                              }
                              setForm(prev => ({
                                ...prev,
                                variants: (prev.variants || []).map((v, i) => i === index
                                  ? { ...v, imageFile: file, imagePreview: URL.createObjectURL(file) }
                                  : v)
                              }));
                              e.target.value = '';
                            }}
                          />
                        </label>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        <div className="space-y-1">
                          <label className={`text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Variant Name</label>
                          <input
                            type="text"
                            value={variant.name}
                            onChange={(e) => handleVariantChange(index, 'name', e.target.value)}
                            placeholder="e.g. Chrome 400mm"
                            className={`w-full px-2 py-1.5 border rounded-lg text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                          />
                        </div>
                        <div className="space-y-1">
                          <label className={`text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>MRP (₹)</label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={variant.mrp}
                            onChange={(e) => handleVariantChange(index, 'mrp', e.target.value)}
                            placeholder="0"
                            className={`w-full px-2 py-1.5 border rounded-lg text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                          />
                        </div>
                        {getActiveOfferTypes(selectedBrand).filter(ot => ot.key !== 'flat').map(ot => (
                          <div className="space-y-1" key={ot.key}>
                            <label className={`text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{ot.label} (₹)</label>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={variant.offerType?.[ot.key] ?? ''}
                              onChange={(e) => handleVariantOfferTypeChange(index, ot.key, e.target.value)}
                              placeholder={ot.label}
                              className={`w-full px-2 py-1.5 border rounded-lg text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                            />
                          </div>
                        ))}
                        {visibleVariantFields.map((field) => (
                          <div className="space-y-1" key={field.fieldName}>
                            <label className={`text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{field.fieldName}</label>
                            {field.fieldType === 'select' ? (
                              <select
                                value={variant.additionalFieldValues?.[field.fieldName] || ''}
                                onChange={(e) => handleVariantAdditionalFieldChange(index, field.fieldName, e.target.value)}
                                className={`w-full px-2 py-1.5 border rounded-lg text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                              >
                                <option value="">Select</option>
                                {(field.options || []).map((opt, i) => (<option key={i} value={opt}>{opt}</option>))}
                              </select>
                            ) : (
                              <input
                                type={field.fieldType === 'number' ? 'number' : 'text'}
                                min={field.min ?? undefined}
                                max={field.max ?? undefined}
                                step={field.decimals ? 1 / Math.pow(10, field.decimals) : undefined}
                                maxLength={field.maxLength || undefined}
                                value={variant.additionalFieldValues?.[field.fieldName] ?? ''}
                                onChange={(e) => handleVariantAdditionalFieldChange(index, field.fieldName, e.target.value)}
                                placeholder={field.fieldName}
                                className={`w-full px-2 py-1.5 border rounded-lg text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                              />
                            )}
                          </div>
                        ))}
                        {subItemOptionList.length > 0 && (
                          <div className="space-y-1 sm:col-span-2 lg:col-span-3">
                            <label className={`text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Preferred Sub-items</label>
                            <Select
                              isMulti
                              options={subItemOptionList}
                              value={subItemOptionList.filter(o => (variant.preferredSubItems || []).map(String).includes(String(o.value)))}
                              onChange={(opts) => handleVariantPreferredChange(index, opts)}
                              placeholder="Select bundle sub-items this variant prefers..."
                              className="basic-multi-select"
                              classNamePrefix="select"
                              styles={customSelectStyles}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          )}

          {/* Enhanced Action Buttons */}
          <div className={`mt-4 sm:mt-6 p-4 rounded-lg border-t flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 transition-all duration-300 col-span-full ${
            darkMode 
              ? 'bg-gradient-to-r from-gray-700/40 to-gray-800/40 border-gray-600 backdrop-blur-sm' 
              : 'bg-gradient-to-r from-gray-50/80 to-gray-100/80 border-gray-200 backdrop-blur-sm'
          }`}>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <button 
                type="submit" 
                className={`group relative bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white px-6 sm:px-8 py-2.5 sm:py-3 rounded-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-2xl transform hover:scale-[1.02] flex items-center justify-center space-x-2 ${
                  darkMode ? 'hover:shadow-indigo-500/25' : 'hover:shadow-indigo-500/20'
                }`}
              >
                {isEditing ? (
                  <>
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Update Item</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                    </svg>
                    <span>Create Item</span>
                  </>
                )}
                <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg blur opacity-0 group-hover:opacity-30 transition-opacity duration-300 -z-10"></div>
              </button>
              {isEditing && (
                <button
                  type="button"
                  onClick={resetForm}
                  className={`px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg font-semibold transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-[1.02] flex items-center justify-center space-x-2 ${
                    darkMode
                      ? 'bg-gray-600 hover:bg-gray-500 text-gray-100'
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
                  }`}
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  <span>Cancel</span>
                </button>
              )}
            </div>
            {isEditing && (
              <div className={`text-xs sm:text-sm px-3 sm:px-4 py-2 rounded-lg flex items-center justify-center sm:justify-start space-x-2 ${
                darkMode ? 'bg-amber-900/30 text-amber-300 border border-amber-700/50' : 'bg-amber-50 text-amber-700 border border-amber-200'
              }`}>
                <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
                <span>Editing Mode</span>
              </div>
            )}
          </div>
        </form>
      </div>

      {/* Items list section */}
      <div className="mt-6 sm:mt-8">
      <div className={`p-4 sm:p-6 shadow-2xl rounded-xl border transition-all duration-300 ${
        darkMode 
          ? 'bg-gradient-to-br from-gray-800 via-gray-800 to-gray-900 border-gray-700' 
          : 'bg-gradient-to-br from-white via-white to-gray-50 border-gray-200'
      }`}>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 gap-3">
          <h2 className={`text-xl sm:text-2xl lg:text-3xl font-bold transition-colors duration-300 ${
            darkMode ? 'text-white' : 'text-gray-800'
          }`}>
            Items
          </h2>
          <div className={`text-xs sm:text-sm px-2 sm:px-3 py-1 rounded-full transition-colors duration-300 ${
            darkMode 
              ? 'bg-gray-700 text-gray-300' 
              : 'bg-gray-100 text-gray-500'
          }`}>
            Total: {totalItems} items
          </div>
        </div>
        
        {/* Enhanced search bar and entries selector */}
        <div className="flex flex-col sm:flex-row lg:flex-row items-start sm:items-center justify-between mb-4 sm:mb-6 space-y-3 sm:space-y-0 sm:space-x-4 lg:space-x-6">
          {/* Search Bar */}
          <div className="w-full sm:w-2/3 lg:w-2/3 relative">
            <div className="absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center pointer-events-none">
              <svg className={`h-4 w-4 sm:h-5 sm:w-5 transition-colors duration-300 ${darkMode ? 'text-gray-400' : 'text-gray-400'}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
              </svg>
            </div>
            <input
              type="text"
              value={filter}
              onChange={handleFilterChange}
              className={`w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-2.5 sm:py-3 border rounded-lg shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm sm:text-base hover:shadow-md ${
                darkMode
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:bg-gray-600 focus:shadow-lg focus:shadow-indigo-500/10'
                  : 'bg-white border-gray-300 text-gray-700 placeholder-gray-500 focus:bg-gray-50 focus:shadow-lg focus:shadow-indigo-500/10'
              }`}
              placeholder="Search by brand name or SKU code..."
            />
            {filter && (
              <button
                onClick={() => {
                  setFilter('');
                  setFilteredItems(items);
                }}
                className={`absolute inset-y-0 right-0 pr-3 sm:pr-4 flex items-center transition-colors duration-200 ${
                  darkMode 
                    ? 'text-gray-400 hover:text-gray-200' 
                    : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                <svg className="h-4 w-4 sm:h-5 sm:w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          {/* Brand Filter */}
          <div className="w-full sm:w-56 lg:w-64 flex-shrink-0">
            <Select
              value={listBrand}
              onChange={handleListBrandChange}
              options={brands}
              placeholder="Filter by brand..."
              isClearable
              className="basic-single"
              classNamePrefix="select"
              styles={customSelectStyles}
            />
          </div>
          
          {/* Entries Selector */}
          <div className={`flex items-center space-x-2 sm:space-x-3 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg border shadow-sm transition-colors duration-300 ${
            darkMode
              ? 'bg-gradient-to-r from-gray-700 to-gray-800 border-gray-600'
              : 'bg-gradient-to-r from-gray-50 to-gray-100 border-gray-200'
          }`}>
            <span className={`text-xs sm:text-sm font-medium whitespace-nowrap transition-colors duration-300 ${
              darkMode ? 'text-gray-200' : 'text-gray-700'
            }`}>
              Show
            </span>
            <div className="w-16 sm:w-20">
              <Select
                value={limitOptions.find(option => option.value === limit)}
                onChange={(selectedOption) => setLimit(selectedOption?.value || 5)}
                options={limitOptions}
                isSearchable={false}
                className="basic-single"
                classNamePrefix="select"
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: '28px',
                    border: 'none',
                    boxShadow: 'none',
                    backgroundColor: darkMode ? '#374151' : 'white',
                    fontSize: '0.875rem',
                    '&:hover': {
                      border: 'none'
                    }
                  }),
                  singleValue: (base) => ({
                    ...base,
                    color: darkMode ? '#F3F4F6' : '#111827',
                    fontSize: '0.875rem'
                  }),
                  menu: (base) => ({
                    ...base,
                    backgroundColor: darkMode ? '#374151' : '#ffffff',
                    border: darkMode ? '1px solid #4B5563' : '1px solid #D1D5DB',
                    fontSize: '0.875rem'
                  }),
                  option: (base, state) => ({
                    ...base,
                    backgroundColor: state.isSelected 
                      ? (darkMode ? '#4F46E5' : '#6366F1')
                      : state.isFocused 
                        ? (darkMode ? '#4B5563' : '#F3F4F6')
                        : (darkMode ? '#374151' : '#ffffff'),
                    color: state.isSelected ? '#ffffff' : (darkMode ? '#F3F4F6' : '#111827'),
                    fontSize: '0.875rem'
                  }),
                  valueContainer: (base) => ({
                    ...base,
                    padding: '0 6px'
                  }),
                  indicatorSeparator: () => ({
                    display: 'none'
                  }),
                  dropdownIndicator: (base) => ({
                    ...base,
                    padding: '2px',
                    color: darkMode ? '#9CA3AF' : '#6B7280'
                  })
                }}
              />
            </div>
            <span className={`text-xs sm:text-sm font-medium whitespace-nowrap transition-colors duration-300 ${
              darkMode ? 'text-gray-200' : 'text-gray-700'
            }`}>
              entries
            </span>
          </div>
        </div>

        {/* View toggle */}
        <div className="flex items-center gap-2 my-2">
          <span className={`text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>View:</span>
          <div className={`inline-flex rounded-lg border overflow-hidden ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}>
            <button type="button" onClick={() => setItemViewMode('list')} className={`px-3 py-1.5 text-xs font-semibold ${itemViewMode === 'list' ? 'bg-indigo-600 text-white' : (darkMode ? 'bg-gray-700 text-gray-200' : 'bg-white text-gray-700')}`}>List</button>
            <button type="button" onClick={() => setItemViewMode('tree')} className={`px-3 py-1.5 text-xs font-semibold ${itemViewMode === 'tree' ? 'bg-indigo-600 text-white' : (darkMode ? 'bg-gray-700 text-gray-200' : 'bg-white text-gray-700')}`} title="Group item → variants → sub-items">Tree</button>
          </div>
        </div>

        {/* Tree view: Item -> Variants -> Sub-items */}
        {itemViewMode === 'tree' && (
          <div className={`overflow-hidden rounded-lg border shadow-sm ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
            {(!filteredItems || filteredItems.length === 0) ? (
              <div className={`px-4 py-8 text-center text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No items.</div>
            ) : (
              <div className={`divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                {filteredItems.map(item => {
                  const open = !!expandedTreeItems[item._id];
                  const variants = item.variants || [];
                  const subs = item.subItems || [];
                  const hasChildren = variants.length > 0 || subs.length > 0;
                  return (
                    <div key={item._id}>
                      <button type="button" onClick={() => hasChildren && toggleTreeItem(item._id)} className={`w-full flex items-center gap-3 px-4 py-3 text-left ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}>
                        <span className={`w-4 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{hasChildren ? (open ? '▾' : '▸') : '•'}</span>
                        <span className={`font-mono text-sm ${darkMode ? 'text-indigo-300' : 'text-indigo-700'}`}>{item.skuCode || '—'}</span>
                        <span className={`flex-1 text-sm truncate ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>{item.skuDescription}</span>
                        <span className={`text-sm font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>₹{Number(item.mrp || 0).toLocaleString()}</span>
                      </button>
                      {open && (
                        <div className={`pl-10 pr-4 pb-3 space-y-2 ${darkMode ? 'bg-gray-900/30' : 'bg-gray-50/60'}`}>
                          {!hasChildren && <div className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>No variants or sub-items.</div>}
                          {variants.length > 0 && (
                            <div>
                              <div className={`text-xs font-bold uppercase tracking-wide opacity-60 mt-2 ${darkMode ? 'text-violet-300' : 'text-violet-700'}`}>Variants</div>
                              <ul className="mt-1 space-y-1">
                                {variants.map((v, i) => (
                                  <li key={i} className={`text-sm flex flex-wrap gap-2 items-center ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                                    <span className="font-mono text-xs opacity-60">{v.variantId || '—'}</span>
                                    <span className="font-medium">{v.name || 'Variant'}</span>
                                    <span className="opacity-70">₹{Number(v.mrp || 0).toLocaleString()}</span>
                                    {(v.preferredSubItems || []).length > 0 && <span className="text-xs opacity-70">prefers: {(v.preferredSubItems || []).map(s => s?.skuCode || s?.id || s?.skuDescription || '').filter(Boolean).join(', ')}</span>}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {subs.length > 0 && (
                            <div>
                              <div className={`text-xs font-bold uppercase tracking-wide opacity-60 mt-2 ${darkMode ? 'text-amber-300' : 'text-amber-700'}`}>Sub-items</div>
                              <ul className="mt-1 space-y-1">
                                {subs.map((s, i) => (
                                  <li key={i} className={`text-sm flex flex-wrap gap-2 items-center ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                                    <span className="font-medium">{s.item?.skuDescription || s.item?.skuCode || 'Sub-item'}</span>
                                    {s.item?.skuCode ? <span className="font-mono text-xs opacity-60">{s.item.skuCode}</span> : null}
                                    <span className="opacity-70">× {s.quantity || 1}</span>
                                    {s.variantName ? <span className="text-xs opacity-70">variant: {s.variantName}</span> : null}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Enhanced TABLE for sm+ screens */}
        <div className={`${itemViewMode === 'tree' ? 'hidden' : 'hidden sm:block'} overflow-hidden rounded-lg border shadow-sm transition-colors duration-300 ${
          darkMode ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className={`transition-all duration-300 ${
              darkMode 
                ? 'bg-gradient-to-r from-gray-700 via-gray-700 to-gray-800 border-b border-gray-600' 
                : 'bg-gradient-to-r from-gray-50 via-gray-50 to-gray-100 border-b border-gray-200'
            }`}>
              <tr>
                <th className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider transition-colors duration-300 ${
                  darkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>
                  Brand
                </th>
                <th className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider transition-colors duration-300 ${
                  darkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>
                  SKU Code
                </th>
                <th className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider transition-colors duration-300 ${
                  darkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>
                  Description
                </th>
                <th className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider transition-colors duration-300 ${
                  darkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className={`divide-y transition-colors duration-300 ${
              darkMode 
                ? 'bg-gray-800 divide-gray-700' 
                : 'bg-white divide-gray-200'
            }`}>
              {filteredItems?.map((item, index) => (
                <tr key={item._id} className={`transition-all duration-200 border-b ${
                  darkMode
                    ? `hover:bg-gray-700/80 hover:shadow-lg border-gray-700/50 ${index % 2 === 0 ? 'bg-gray-800' : 'bg-gray-800/70'}`
                    : `hover:bg-gray-50/80 hover:shadow-md border-gray-100 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-25'}`
                }`}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className={`text-sm font-medium transition-colors duration-300 ${
                      darkMode ? 'text-gray-100' : 'text-gray-900'
                    }`}>
                      {item.brand?.name}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className={`text-sm font-mono px-2 py-1 rounded transition-colors duration-300 ${
                      darkMode 
                        ? 'text-gray-100 bg-gray-700' 
                        : 'text-gray-900 bg-gray-100'
                    }`}>
                      {item.skuCode}
                    </div>
                    {item.isActive === false && (
                      <span className={`inline-block mt-1 px-2 py-0.5 text-[10px] font-bold uppercase rounded ${
                        darkMode ? 'bg-red-900 text-red-300' : 'bg-red-100 text-red-700'
                      }`}>
                        Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className={`text-sm max-w-xs truncate transition-colors duration-300 ${
                      darkMode ? 'text-gray-200' : 'text-gray-900'
                    }`} title={item.skuDescription}>
                      {item.skuDescription}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-150"
                        onClick={() => handleEdit(item)}
                      >
                        Edit
                      </button>
                      <button
                        className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors duration-150"
                        onClick={() => generateQRCode(item)}
                        title="Generate QR Code"
                      >
                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                        </svg>
                        QR
                      </button>
                      <button
                        className={`inline-flex items-center px-3 py-1.5 border text-xs font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-150 ${
                          darkMode
                            ? 'border-gray-600 text-gray-200 bg-gray-700 hover:bg-gray-600'
                            : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                        }`}
                        onClick={() => showItemDetails(item)}
                      >
                        Details
                      </button>
                      {item.updatedBy && item.updatedBy.length > 0 && (
                        <button
                          className="inline-flex items-center px-3 py-1.5 border border-amber-300 text-xs font-medium rounded-md text-amber-700 bg-amber-50 hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 transition-colors duration-150"
                          onClick={() => showUpdateHistory(item)}
                          title={`View ${item.updatedBy.length} updates`}
                        >
                          <ClockIcon className="w-3 h-3 mr-1" />
                          History ({item.updatedBy.length})
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Enhanced GRID CARDS for mobile devices */}
        <div className={`${itemViewMode === 'tree' ? 'hidden' : 'sm:hidden'} grid grid-cols-1 gap-4`}>
          {filteredItems?.map((item) => (
            <div key={item._id} className={`shadow-lg rounded-xl p-4 border transition-all duration-300 transform hover:scale-[1.02] ${
              darkMode
                ? 'bg-gradient-to-br from-gray-800 to-gray-900 border-gray-700 hover:shadow-2xl hover:shadow-gray-900/20 hover:border-gray-600'
                : 'bg-gradient-to-br from-white to-gray-50 border-gray-200 hover:shadow-2xl hover:shadow-gray-900/10 hover:border-gray-300'
            }`}>
              <div className="flex justify-between items-start mb-3">
                <h3 className={`text-lg font-bold truncate transition-colors duration-300 ${
                  darkMode ? 'text-white' : 'text-gray-800'
                }`}>
                  {item.skuDescription}
                </h3>
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  item.series === 'RS' ? (darkMode ? 'bg-green-900/30 text-green-300 border border-green-700' : 'bg-green-100 text-green-800 border border-green-200') :
                  item.series === 'AO' ? (darkMode ? 'bg-blue-900/30 text-blue-300 border border-blue-700' : 'bg-blue-100 text-blue-800 border border-blue-200') :
                  item.series === 'SC' ? (darkMode ? 'bg-yellow-900/30 text-yellow-300 border border-yellow-700' : 'bg-yellow-100 text-yellow-800 border border-yellow-200') :
                  item.series === 'DC' ? (darkMode ? 'bg-red-900/30 text-red-300 border border-red-700' : 'bg-red-100 text-red-800 border border-red-200') :
                  item.series === 'ITF' ? (darkMode ? 'bg-purple-900/30 text-purple-300 border border-purple-700' : 'bg-purple-100 text-purple-800 border border-purple-200') :
                  (darkMode ? 'bg-gray-700 text-gray-300 border border-gray-600' : 'bg-gray-100 text-gray-800 border border-gray-200')
                }`}>
                  {seriesOptions.find(opt => opt.value === item.series)?.label || item.series}
                </span>
              </div>
              <div className="space-y-2 mb-4">
                <div className="flex justify-between">
                  <span className={`text-sm font-medium transition-colors duration-300 ${
                    darkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    Brand:
                  </span>
                  <span className={`text-sm transition-colors duration-300 ${
                    darkMode ? 'text-gray-200' : 'text-gray-900'
                  }`}>
                    {item.brand?.name}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className={`text-sm font-medium transition-colors duration-300 ${
                    darkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    SKU Code:
                  </span>
                  <span className={`text-sm font-mono px-2 py-0.5 rounded transition-colors duration-300 ${
                    darkMode 
                      ? 'text-gray-100 bg-gray-700' 
                      : 'text-gray-900 bg-gray-100'
                  }`}>
                    {item.skuCode}
                  </span>
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  className="flex-1 inline-flex justify-center items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-150"
                  onClick={() => handleEdit(item)}
                >
                  Edit
                </button>
                <button
                  className="flex-1 inline-flex justify-center items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors duration-150"
                  onClick={() => generateQRCode(item)}
                  title="Generate QR Code"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                  </svg>
                  QR
                </button>
                <button
                  className={`flex-1 inline-flex justify-center items-center px-3 py-2 border text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-150 ${
                    darkMode
                      ? 'border-gray-600 text-gray-200 bg-gray-700 hover:bg-gray-600'
                      : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                  }`}
                  onClick={() => showItemDetails(item)}
                >
                  Details
                </button>
                {item.updatedBy && item.updatedBy.length > 0 && (
                  <button
                    className="flex-1 inline-flex justify-center items-center px-3 py-2 border border-amber-300 text-sm font-medium rounded-md text-amber-700 bg-amber-50 hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 transition-colors duration-150"
                    onClick={() => showUpdateHistory(item)}
                    title={`View ${item.updatedBy.length} updates`}
                  >
                    <ClockIcon className="w-4 h-4 mr-1" />
                    History
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Enhanced Pagination controls */}
        <div className="flex flex-col sm:flex-row justify-between items-center mt-6 sm:mt-8 space-y-3 sm:space-y-0">
          {/* Pagination info */}
          <div className={`text-xs sm:text-sm transition-colors duration-300 order-2 sm:order-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Showing <span className={`font-semibold transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{page * limit + 1}</span> to{' '}
            <span className={`font-semibold transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{Math.min((page + 1) * limit, totalItems)}</span> of{' '}
            <span className={`font-semibold transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{totalItems}</span> entries
          </div>
          
          {/* Pagination buttons */}
          <div className="flex items-center space-x-0.5 sm:space-x-1 order-1 sm:order-2">
            <button
              onClick={() => setPage(page - 1)}
              disabled={page === 0}
              className={`relative inline-flex items-center px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium border rounded-l-md focus:z-10 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 ${
                darkMode
                  ? 'text-gray-300 bg-gray-700 border-gray-600 hover:bg-gray-600 hover:shadow-md disabled:bg-gray-800'
                  : 'text-gray-500 bg-white border-gray-300 hover:bg-gray-50 hover:shadow-md disabled:bg-gray-100'
              }`}
            >
              <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-0.5 sm:mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
              <span className="hidden sm:inline">Previous</span>
              <span className="sm:hidden">Prev</span>
            </button>
            
            {/* Page numbers */}
            {[page - 1, page, page + 1].map((index) =>
              index >= 0 && index < totalPages ? (
                <button
                  key={index}
                  onClick={() => setPage(index)}
                  className={`relative inline-flex items-center px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium border transition-colors duration-150 ${
                    page === index
                      ? 'z-10 bg-indigo-600 border-indigo-600 text-white'
                      : darkMode
                        ? 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600 focus:z-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500'
                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500'
                  }`}
                >
                  {index + 1}
                </button>
              ) : null
            )}
            
            <button
              onClick={() => setPage(page + 1)}
              disabled={page === totalPages - 1}
              className={`relative inline-flex items-center px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium border rounded-r-md focus:z-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150 ${
                darkMode
                  ? 'text-gray-300 bg-gray-700 border-gray-600 hover:bg-gray-600'
                  : 'text-gray-500 bg-white border-gray-300 hover:bg-gray-50'
              }`}
            >
              <span className="hidden sm:inline">Next</span>
              <span className="sm:hidden">Next</span>
              <svg className="w-3 h-3 sm:w-4 sm:h-4 ml-0.5 sm:ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Stylish Modal for Item Details */}
      {modalOpen && modalItem && (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
    {/* Enhanced background overlay with blur effect */}
    <div
      className={`absolute inset-0 backdrop-blur-md transition-all duration-300 ${
        darkMode 
          ? 'bg-black/70' 
          : 'bg-black/50'
      }`}
      onClick={() => setModalOpen(false)}
    />

    {/* Modal container - optimized for tablet screens with reduced height */}
    <div className={`relative w-full max-w-4xl max-h-[75vh] rounded-2xl shadow-2xl overflow-hidden animate-pulse-scale flex flex-col border ${
      darkMode 
        ? 'bg-gradient-to-br from-gray-800 via-gray-800 to-gray-900 border-gray-700 shadow-black/50' 
        : 'bg-gradient-to-br from-white via-white to-gray-50 border-gray-200 shadow-gray-900/20'
    }`}>
      
      {/* Header with gradient background */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white px-6 py-3">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold">Item Details</h3>
            <p className="text-purple-100 text-xs mt-1">{modalItem.skuCode}</p>
          </div>
          <button
            className="text-white hover:text-gray-200 transition-colors duration-200 p-2 rounded-full hover:bg-white hover:bg-opacity-20"
            onClick={() => setModalOpen(false)}
            aria-label="Close modal"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Modal content with better spacing */}
      <div className={`flex-1 p-6 overflow-auto ${
        darkMode ? 'scrollbar-dark' : 'scrollbar-light'
      }`}
           style={{ maxHeight: 'calc(75vh - 140px)' }}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Image section - takes 1 column */}
          <div className="lg:col-span-1">
            <div className={`rounded-xl p-5 shadow-lg border ${
              darkMode 
                ? 'bg-gradient-to-br from-gray-700 to-gray-600 border-gray-600' 
                : 'bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200'
            }`}>
              <h4 className={`text-base font-semibold mb-4 text-center ${
                darkMode ? 'text-gray-200' : 'text-gray-800'
              }`}>Product Image</h4>
              <div className={`aspect-square rounded-xl shadow-lg overflow-hidden ring-2 ${
                darkMode ? 'bg-gray-900 ring-gray-600' : 'bg-white ring-gray-200'
              }`}>
                <img
                  src={viewImages[viewImageIndex]?.url || `${apiBase}/items/image/${modalItem._id}`}
                  alt={modalItem.skuDescription}
                  className="w-full h-full object-cover transition-transform duration-300"
                />
              </div>
              {viewImages.length > 1 && (
                <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
                  {viewImages.map((img, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setViewImageIndex(i)}
                      className={`shrink-0 rounded-lg overflow-hidden ring-2 ${i === viewImageIndex ? 'ring-indigo-500' : 'ring-transparent'}`}
                    >
                      <img src={img.url} alt={`${modalItem.skuDescription} ${i + 1}`} className="w-14 h-14 object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Details section - takes 2 columns */}
          <div className="lg:col-span-2 space-y-5">
            
            {/* Basic Information */}
            <div className={`rounded-xl p-4 border ${
              darkMode
                ? 'bg-gradient-to-r from-indigo-900/50 to-purple-900/50 border-indigo-700'
                : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200'
            }`}>
              <h4 className={`text-base font-bold mb-3 flex items-center ${
                darkMode ? 'text-gray-200' : 'text-gray-800'
              }`}>
                <svg className={`w-4 h-4 mr-2 ${darkMode ? 'text-indigo-400' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Basic Information
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className={`rounded-lg p-3 shadow-sm border ${
                  darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'
                }`}>
                  <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Brand</p>
                  <p className={`text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>{displayValue(modalItem.brand?.name)}</p>
                </div>
                <div className={`rounded-lg p-3 shadow-sm border ${
                  darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'
                }`}>
                  <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Category</p>
                  <p className={`text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>{displayValue(modalItem.category?.name)}</p>
                </div>
                <div className={`rounded-lg p-3 shadow-sm border md:col-span-2 ${
                  darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'
                }`}>
                  <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Description</p>
                  <p className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>{displayValue(modalItem.skuDescription)}</p>
                </div>
              </div>
            </div>

            {/* Sub-items / Add-ons */}
            {modalItem.subItems && modalItem.subItems.length > 0 && (
              <div className={`rounded-xl p-4 border ${
                darkMode
                  ? 'bg-gradient-to-r from-amber-900/30 to-orange-900/30 border-amber-700'
                  : 'bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200'
              }`}>
                <h4 className={`text-base font-bold mb-3 flex items-center ${
                  darkMode ? 'text-amber-300' : 'text-amber-700'
                }`}>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                  Included Sub-items / Add-ons
                </h4>
                <ul className="space-y-2">
                  {modalItem.subItems.map((s, i) => (
                    <li key={i} className={`flex items-center justify-between rounded-lg border px-3 py-2 ${
                      darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'
                    }`}>
                      <span className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                        {s.item?.skuDescription || s.item?.skuCode || (typeof s.item === 'string' ? s.item : 'Sub-item')}
                        {s.item?.skuCode && s.item?.skuDescription ? <span className="opacity-60"> ({s.item.skuCode})</span> : null}
                        {s.variantName ? <span className={`ml-1 ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>· {s.variantName}</span> : null}
                      </span>
                      <span className={`text-sm font-semibold ${darkMode ? 'text-amber-300' : 'text-amber-700'}`}>× {s.quantity || 1}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Variants */}
            {modalItem.variants && modalItem.variants.length > 0 && (
              <div className={`rounded-xl p-4 border ${
                darkMode
                  ? 'bg-gradient-to-r from-violet-900/30 to-indigo-900/30 border-violet-700'
                  : 'bg-gradient-to-r from-violet-50 to-indigo-50 border-violet-200'
              }`}>
                <h4 className={`text-base font-bold mb-3 flex items-center ${
                  darkMode ? 'text-violet-300' : 'text-violet-700'
                }`}>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h7" />
                  </svg>
                  Variants
                </h4>
                <ul className="space-y-2">
                  {modalItem.variants.map((v, i) => {
                    const subName = (id) => {
                      const s = (modalItem.subItems || []).find(x =>
                        String(typeof x.item === 'object' ? x.item._id : x.item) === String(id));
                      return s ? (typeof s.item === 'object' ? (s.item.skuDescription || s.item.skuCode) : s.item) : String(id);
                    };
                    return (
                      <li key={i} className={`rounded-lg border px-3 py-2 ${
                        darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'
                      }`}>
                        <div className="flex items-center justify-between">
                          <span className={`text-sm font-medium ${darkMode ? 'text-violet-300' : 'text-violet-700'}`}>
                            {v.variantId ? <span className="opacity-60 font-mono mr-1">{v.variantId}</span> : null}
                            {v.name || 'Variant'}
                          </span>
                          <span className={`text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                            ₹{(v.mrp || 0).toLocaleString()}
                          </span>
                        </div>
                        {(v.preferredSubItems || []).length > 0 && (
                          <p className={`text-xs mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Preferred: {(v.preferredSubItems || []).map(subName).join(', ')}
                          </p>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {/* Technical Details */}
            <div className={`rounded-xl p-4 border ${
              darkMode
                ? 'bg-gradient-to-r from-green-900/50 to-emerald-900/50 border-green-700'
                : 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200'
            }`}>
              <h4 className={`text-base font-bold mb-3 flex items-center ${
                darkMode ? 'text-gray-200' : 'text-gray-800'
              }`}>
                <svg className={`w-4 h-4 mr-2 ${darkMode ? 'text-green-400' : 'text-green-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Technical Details
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className={`rounded-lg p-3 shadow-sm border ${
                  darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'
                }`}>
                  <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>SKU Code</p>
                  <p className={`text-xs font-mono px-2 py-1 rounded-full ${
                    darkMode ? 'bg-gray-600 text-gray-200' : 'bg-gray-100 text-gray-800'
                  }`}>{displayValue(modalItem.skuCode)}</p>
                </div>
                <div className={`rounded-lg p-3 shadow-sm border ${
                  darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'
                }`}>
                  <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Series</p>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    modalItem.series === 'RS' ? (darkMode ? 'bg-green-900 text-green-200 border border-green-700' : 'bg-green-100 text-green-800 border border-green-300') :
                    modalItem.series === 'AO' ? (darkMode ? 'bg-blue-900 text-blue-200 border border-blue-700' : 'bg-blue-100 text-blue-800 border border-blue-300') :
                    modalItem.series === 'SC' ? (darkMode ? 'bg-yellow-900 text-yellow-200 border border-yellow-700' : 'bg-yellow-100 text-yellow-800 border border-yellow-300') :
                    modalItem.series === 'DC' ? (darkMode ? 'bg-red-900 text-red-200 border border-red-700' : 'bg-red-100 text-red-800 border border-red-300') :
                    modalItem.series === 'ITF' ? (darkMode ? 'bg-purple-900 text-purple-200 border border-purple-700' : 'bg-purple-100 text-purple-800 border border-purple-300') :
                    (darkMode ? 'bg-gray-700 text-gray-200 border border-gray-600' : 'bg-gray-100 text-gray-800 border border-gray-300')
                  }`}>
                    {displayValue(modalItem.series)}
                  </span>
                </div>
                <div className={`rounded-lg p-3 shadow-sm border ${
                  darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'
                }`}>
                  <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>HSN Code</p>
                  <p className={`text-xs font-mono px-2 py-1 rounded-full ${
                    darkMode ? 'bg-gray-600 text-gray-200' : 'bg-gray-100 text-gray-800'
                  }`}>{displayValue(modalItem.hsnCode)}</p>
                </div>
                <div className={`rounded-lg p-3 shadow-sm border ${
                  darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'
                }`}>
                  <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>GST Rate</p>
                  <p className={`text-sm font-bold ${darkMode ? 'text-orange-400' : 'text-orange-600'}`}>
                    {isValidValue(modalItem.gstRate) ? `${modalItem.gstRate}%` : 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            {/* Pricing Information */}
            <div className={`rounded-xl p-4 border ${
              darkMode
                ? 'bg-gradient-to-r from-purple-900/50 to-pink-900/50 border-purple-700'
                : 'bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200'
            }`}>
              <h4 className={`text-base font-bold mb-3 flex items-center ${
                darkMode ? 'text-gray-200' : 'text-gray-800'
              }`}>
                <svg className={`w-4 h-4 mr-2 ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
                Pricing Details
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {/* MRP - always show */}
                <div className={`rounded-lg p-3 shadow-sm border-2 ${
                  darkMode ? 'bg-gray-700 border-green-700' : 'bg-white border-green-200'
                }`}>
                  <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>MRP</p>
                  <p className="text-sm font-bold text-green-600">
                    ₹{displayValue(modalItem.mrp)}
                  </p>
                </div>
                
                {/* Only show offer types that have meaningful values */}
                {(modalItem.brand?.offerTypes && modalItem.brand.offerTypes.length
                  ? modalItem.brand.offerTypes.filter(ot => ot.active !== false)
                  : Object.keys(modalItem.offerType || {})
                ).map((ot) => {
                  const key = typeof ot === 'string' ? ot : ot.key;
                  const label = typeof ot === 'string' ? ot.toUpperCase() : (ot.label || key.toUpperCase());
                  const val = modalItem.offerType?.[key];
                  if (!isValidValue(val)) return null;
                  return (
                    <div key={key} className={`rounded-lg p-3 shadow-sm border ${
                      darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'
                    }`}>
                      <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {label}{key === 'flat' ? ' (= MRP)' : ''}
                      </p>
                      <p className={`text-sm font-semibold ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>₹{val}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Additional Fields */}
            {modalItem.brand?.additionalFields?.length > 0 && modalItem.additionalFieldValues && Object.keys(modalItem.additionalFieldValues).length > 0 && (
              <div className={`rounded-xl p-4 border ${
                darkMode
                  ? 'bg-gradient-to-r from-amber-900/50 to-orange-900/50 border-amber-700'
                  : 'bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200'
              }`}>
                <h4 className={`text-base font-bold mb-3 flex items-center ${
                  darkMode ? 'text-gray-200' : 'text-gray-800'
                }`}>
                  <svg className={`w-4 h-4 mr-2 ${darkMode ? 'text-amber-400' : 'text-amber-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                  Additional Fields
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {modalItem.brand.additionalFields
                    .filter(f => !(f.categories && f.categories.length) ||
                      (modalItem.category && f.categories.map(String).includes(String(modalItem.category._id || modalItem.category))))
                    .map(field => {
                    const val = modalItem.additionalFieldValues[field.fieldName];
                    if (val === undefined || val === null || val === '') return null;
                    return (
                      <div key={field.fieldName} className={`rounded-lg p-3 shadow-sm border ${
                        darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'
                      }`}>
                        <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          {field.fieldName}
                        </p>
                        <p className={`text-sm font-semibold ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                          {String(val)}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Similar Items */}
            {similarItems.length > 0 && (
              <div className={`rounded-xl p-4 border ${
                darkMode
                  ? 'bg-gradient-to-r from-purple-900/50 to-indigo-900/50 border-purple-700'
                  : 'bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200'
              }`}>
                <h4 className={`text-base font-bold mb-3 flex items-center ${
                  darkMode ? 'text-gray-200' : 'text-gray-800'
                }`}>
                  <svg className={`w-4 h-4 mr-2 ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-1.13a4 4 0 10-4 0 4 4 0 004 0z" />
                  </svg>
                  Similar Items
                  <span className={`ml-2 text-xs font-normal ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {similarItems.length}
                  </span>
                </h4>
                <div className="space-y-2">
                  {similarItems.map(similar => {
                    const fields = modalItem.brand?.additionalFields || [];
                    const diffs = fields
                      .map(field => {
                        const current = modalItem.additionalFieldValues?.[field.fieldName];
                        const other = similar.additionalFieldValues?.[field.fieldName];
                        const hasVal = other !== undefined && other !== null && other !== '';
                        return hasVal && String(current) !== String(other)
                          ? `${field.fieldName}: ${other}`
                          : null;
                      })
                      .filter(Boolean);
                    return (
                      <button
                        key={similar._id}
                        type="button"
                        onClick={() => showItemDetails(similar)}
                        className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-colors ${
                          darkMode
                            ? 'bg-gray-700 border-gray-600 hover:bg-gray-600'
                            : 'bg-white border-gray-200 hover:bg-purple-50'
                        }`}>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-semibold truncate ${darkMode ? 'text-purple-300' : 'text-purple-600'}`}>
                            {similar.skuCode || similar._id}
                          </p>
                          <p className={`text-xs truncate ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                            {similar.skuDescription}
                          </p>
                          {diffs.length > 0 && (
                            <p className={`text-xs truncate mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {diffs.join(' · ')}
                            </p>
                          )}
                        </div>
                        <svg className="w-5 h-5 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Audit Information */}
            {(modalItem.createdBy || modalItem.updatedBy || modalItem.createdAt || modalItem.lastUpdatedAt) && (
              <div className={`rounded-xl p-4 border ${
                darkMode
                  ? 'bg-gradient-to-r from-gray-700 to-gray-600 border-gray-600'
                  : 'bg-gradient-to-r from-gray-50 to-slate-50 border-gray-200'
              }`}>
                <h4 className={`text-base font-bold mb-3 flex items-center ${
                  darkMode ? 'text-gray-200' : 'text-gray-800'
                }`}>
                  <svg className={`w-4 h-4 mr-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Audit Trail
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mb-4">
                  <div className={`p-3 rounded-lg border ${
                    darkMode ? 'bg-gray-600 border-gray-500 text-gray-200' : 'bg-gray-50 border-gray-200'
                  }`}>
                    <p className={`font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Created At</p>
                    <p className={darkMode ? 'text-gray-200' : ''}>{modalItem.createdAt ? new Date(modalItem.createdAt).toLocaleString() : 'N/A'}</p>
                  </div>
                  <div className={`p-3 rounded-lg border ${
                    darkMode ? 'bg-gray-600 border-gray-500 text-gray-200' : 'bg-gray-50 border-gray-200'
                  }`}>
                    <p className={`font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Last Updated At</p>
                    <p className={darkMode ? 'text-gray-200' : ''}>{modalItem.lastUpdatedAt ? new Date(modalItem.lastUpdatedAt).toLocaleString() : 'N/A'}</p>
                  </div>
                </div>

                {modalItem.updatedBy && modalItem.updatedBy.length > 0 && (
                  <div>
                    <h4 className={`text-md font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Update History</h4>
                    <div className="max-h-60 overflow-y-auto space-y-2 pr-2">
                      {modalItem.updatedBy.slice().reverse().map(log => {
                        const updaterName = (log.employee ? log.employee.name : '') || (log.employee ? log.employee.email : '') || (typeof log.employee === 'string' ? log.employee : 'Unknown');
                        
                        const keyChanges = {};
                        if (log.pastData) {
                          if (log.pastData.mrp !== undefined) keyChanges.MRP = log.pastData.mrp;
                          if (log.pastData.gstRate !== undefined) keyChanges['GST %'] = log.pastData.gstRate;
                          if (log.pastData.offerType) {
                            Object.entries(log.pastData.offerType).forEach(([key, value]) => {
                              if (value !== undefined && value !== null && value !== '') {
                                keyChanges[key.toUpperCase()] = value;
                              }
                            });
                          }
                        }

                        return (
                          <div key={log._id} className={`p-3 rounded-lg border text-xs ${
                            darkMode ? 'bg-gray-600 border-gray-500' : 'bg-gray-50 border-gray-200'
                          }`}>
                            <div className="flex justify-between items-start">
                              <div>
                                <p className={`font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>{updaterName}</p>
                                <p className={darkMode ? 'text-gray-400' : 'text-gray-500'}>{new Date(log.date).toLocaleString()}</p>
                              </div>
                              {log.pastData && Object.keys(log.pastData).length > 0 && (
                                <button 
                                  onClick={() => toggleUpdateDetails(log._id)}
                                  className={`text-sm font-medium ${
                                    darkMode 
                                      ? 'text-blue-400 hover:text-blue-300' 
                                      : 'text-blue-600 hover:text-blue-800'
                                  }`}
                                >
                                  {expandedUpdates[log._id] ? 'Hide Data' : 'View Data'}
                                </button>
                              )}
                            </div>

                            {Object.keys(keyChanges).length > 0 && (
                              <div className={`mt-2 pt-2 border-t ${darkMode ? 'border-gray-500' : 'border-gray-200'}`}>
                                <p className={`font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Key Previous Values:</p>
                                <div className={`grid grid-cols-2 sm:grid-cols-3 gap-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                  {Object.entries(keyChanges).map(([key, value]) => (
                                    <div key={key}>
                                      <span className="font-semibold">{key}:</span> {value}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {expandedUpdates[log._id] && log.pastData && (
                              <div className={`mt-2 pt-2 border-t ${darkMode ? 'border-gray-500' : 'border-gray-200'}`}>
                                <p className={`font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Full Previous Data:</p>
                                <pre className={`p-2 rounded border text-xs whitespace-pre-wrap break-all ${
                                  darkMode 
                                    ? 'bg-gray-700 border-gray-600 text-gray-300' 
                                    : 'bg-white border-gray-200 text-gray-700'
                                }`}>
                                  {JSON.stringify(
                                    (({ image, updatedBy, __v, ...rest }) => rest)(log.pastData), 
                                    null, 
                                    2
                                  )}
                                </pre>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
            {/* End Audit Section */}
          </div>{/* end details column */}
        </div>{/* end grid */}
      </div>{/* end content scroll area */}

      {/* Enhanced footer - now fixed at bottom */}
      <div className={`flex-shrink-0 px-6 py-4 border-t ${
        darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
      }`}>
        <div className="flex flex-col sm:flex-row justify-between items-center gap-3">
          <div className={`text-xs order-2 sm:order-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Item ID: {modalItem._id}
          </div>
          <div className="flex space-x-3 order-1 sm:order-2">
            <button
              onClick={() => {
                handleEdit(modalItem);
                setModalOpen(false);
              }}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center space-x-2 shadow-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              <span>Edit Item</span>
            </button>
            <button
              onClick={() => {
                generateQRCode(modalItem);
                setModalOpen(false);
              }}
              className="bg-purple-600 hover:bg-purple-700 text-white px-5 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center space-x-2 shadow-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
              </svg>
              <span>QR Code</span>
            </button>
            <button
              onClick={() => setModalOpen(false)}
              className="bg-gray-600 hover:bg-gray-700 text-white px-5 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 shadow-sm"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
)}

      {/* Progress Modal */}
      <ProgressModal
        isOpen={progressModal.isOpen}
        onClose={() => setProgressModal(prev => ({ ...prev, isOpen: false }))}
        title={progressModal.title}
        steps={progressModal.steps}
        currentStep={progressModal.currentStep}
        progress={progressModal.progress}
        message={progressModal.message}
      />

      {/* UpdatedBy History Modal */}
      <UpdatedByModal
        isOpen={updatedByModal.isOpen}
        onClose={() => setUpdatedByModal({ isOpen: false, item: null })}
        item={updatedByModal.item}
        darkMode={darkMode}
      />

      {/* Excel Import Modal */}
      {showImport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className={`w-full max-w-3xl rounded-xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col ${
            darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white'
          }`}>
            <div className={`flex items-center justify-between p-6 rounded-t-xl ${
              darkMode ? 'bg-gradient-to-r from-blue-800 to-indigo-800' : 'bg-gradient-to-r from-blue-600 to-blue-700'
            }`}>
              <div>
                <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-white'}`}>Import Items from Excel</h2>
                <p className={`${darkMode ? 'text-blue-200' : 'text-blue-100'} mt-1`}>Upload and process Excel files with item pricing data</p>
              </div>
              <button
                onClick={() => {
                  setShowImport(false);
                  setImportBrand(null);
                  setImportFile(null);
                  setImportResult(null);
                  setPreviewChecked(false);
                  setPreviewData(null);
                  setShowPreview(false);
                  setImportCategory(null);
                  setImportSeries({ value: 'RS', label: 'Regular Stock (RS)' });
                  setApplyGstToOffers(false);
                  setGstOfferTypes([]);
                }}
                className="text-blue-100 hover:text-white p-2 rounded-lg hover:bg-blue-800 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              {!importResult ? (
                <div className="space-y-6">
                  {/* Download Template Section */}
                  <div className={`border-l-4 ${darkMode ? 'border-blue-400 bg-blue-900/20' : 'border-blue-500 bg-blue-50'} p-4 rounded-r-lg`}>
                    <div className="flex items-center">
                      <svg className={`w-5 h-5 ${darkMode ? 'text-blue-400' : 'text-blue-600'} mr-3`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      <div className="flex-1">
                        <h4 className={`font-semibold ${darkMode ? 'text-blue-300' : 'text-blue-800'}`}>Download Template First</h4>
                        <p className={`${darkMode ? 'text-blue-400' : 'text-blue-700'} text-sm`}>Three simple tabs: Products, Variants, Accessories (with example rows on separate Example tabs).</p>
                      </div>
                      <button
                        onClick={async () => {
                          if (!importBrand) {
                            toast.warning('Select a brand first to download the template.');
                            return;
                          }
                          try {
                            const token = localStorage.getItem('token');
                            const brandId = importBrand.value;
                            const categoryId = importCategory?.value;
                            const params = new URLSearchParams({ brandId });
                            if (categoryId) params.append('categoryId', categoryId);
                            const response = await fetch(
                              `${process.env.REACT_APP_API_BASE_URL}/reports/items/import-template?${params.toString()}`,
                              { headers: { 'Authorization': `Bearer ${token}` } }
                            );
                            if (!response.ok) throw new Error('Failed to download template');
                            const blob = await response.blob();
                            const url = window.URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `items_import_template_${brandId}${categoryId ? `_${categoryId}` : ''}.xlsx`;
                            document.body.appendChild(a);
                            a.click();
                            window.URL.revokeObjectURL(url);
                            document.body.removeChild(a);
                            toast.success('Excel template downloaded successfully.');
                          } catch (err) {
                            toast.error(err.message || 'Could not download template.');
                          }
                        }}
                        disabled={!importBrand}
                        className={`${darkMode ? 'bg-blue-700 hover:bg-blue-600' : 'bg-blue-600 hover:bg-blue-700'} text-white py-2 px-4 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        Download Template
                      </button>
                    </div>
                  </div>

                  {/* Brand Selection */}
                  <div>
                    <label className={`block text-sm font-bold mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                      Brand Selection <span className="text-red-500">*</span>
                    </label>
                    <Select
                      isClearable
                      options={brands.map(b => ({ value: b.value, label: b.label }))}
                      value={importBrand}
                      onChange={(val) => {
                        setImportBrand(val);
                        setImportCategory(null);
                        const ots = (brands.find(b => b.value === val?.value)?.offerTypes || [])
                          .filter(ot => ot.active !== false && ot.key !== 'flat');
                        setGstOfferTypes(ots.map(ot => ot.key));
                        setPreviewChecked(false);
                        setPreviewData(null);
                        setShowPreview(false);
                      }}
                      placeholder="Choose a brand..."
                      className="basic-select"
                      classNamePrefix="select"
                      styles={{
                        control: (base) => ({
                          ...base,
                          borderRadius: '0.5rem',
                          border: `2px solid ${darkMode ? '#4B5563' : '#E5E7EB'}`,
                          backgroundColor: darkMode ? '#1F2937' : '#fff',
                          '&:hover': { borderColor: '#3B82F6' },
                        }),
                        indicatorSeparator: () => ({ display: 'none' }),
                      }}
                    />
                  </div>

                  {/* Category Selection */}
                  <div>
                    <label className={`block text-sm font-bold mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                      Category Selection <span className="text-red-500">*</span>
                    </label>
                    <Select
                      isClearable
                      options={(brands.find(b => b.value === importBrand?.value)?.categories || []).map(c => ({ value: c._id, label: c.name }))}
                      value={importCategory}
                      onChange={(val) => {
                        setImportCategory(val);
                        setPreviewChecked(false);
                      }}
                      isDisabled={!importBrand}
                      placeholder={importBrand ? 'Choose a category...' : 'Select a brand first'}
                      className="basic-select"
                      classNamePrefix="select"
                      noOptionsMessage={() => importBrand ? 'No categories found' : 'Select a brand first'}
                      styles={{
                        control: (base) => ({
                          ...base,
                          borderRadius: '0.5rem',
                          border: `2px solid ${darkMode ? '#4B5563' : '#E5E7EB'}`,
                          backgroundColor: darkMode ? '#1F2937' : '#fff',
                          '&:hover': { borderColor: '#3B82F6' },
                        }),
                        indicatorSeparator: () => ({ display: 'none' }),
                      }}
                    />
                    {importBrand && brands.find(b => b.value === importBrand?.value)?.advancedItems && (
                      <p className={`mt-2 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Default category - rows with their own Category (advanced brands) override it.
                      </p>
                    )}
                  </div>

                  {/* Series Selection */}
                  <div>
                    <label className={`block text-sm font-bold mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                      Series
                    </label>
                    <Select
                      options={seriesOptions}
                      value={importSeries}
                      onChange={(val) => { setImportSeries(val); setPreviewChecked(false); }}
                      isSearchable={false}
                      placeholder="Choose a series..."
                      className="basic-select"
                      classNamePrefix="select"
                      styles={{
                        control: (base) => ({
                          ...base,
                          borderRadius: '0.5rem',
                          border: `2px solid ${darkMode ? '#4B5563' : '#E5E7EB'}`,
                          backgroundColor: darkMode ? '#1F2937' : '#fff',
                          '&:hover': { borderColor: '#3B82F6' },
                        }),
                        indicatorSeparator: () => ({ display: 'none' }),
                      }}
                    />
                  </div>

                  {/* GST to offers */}
                  <div className={`flex items-start gap-3 p-4 border rounded-lg ${darkMode ? 'bg-gray-800 border-gray-600' : 'bg-gray-50'}`}>
                    <input
                      id="employee-apply-gst-offers"
                      type="checkbox"
                      checked={applyGstToOffers}
                      onChange={(e) => setApplyGstToOffers(e.target.checked)}
                      className={`mt-1 h-5 w-5 text-blue-600 ${darkMode ? 'border-gray-600' : 'border-gray-300'} rounded focus:ring-blue-500`}
                    />
                    <label htmlFor="employee-apply-gst-offers" className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                      <span className="font-semibold">Add GST to offer prices on import</span>
                      <span className={`block text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'} mt-1`}>
                        When enabled, selected offer type values will be increased by the item's GST rate (%). MRP remains unchanged.
                      </span>
                    </label>
                  </div>

                  {applyGstToOffers && (
                    <div className={`p-4 border rounded-lg ${darkMode ? 'bg-gray-800 border-gray-600' : 'bg-white'}`}>
                      <p className={`text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-700'} mb-3`}>Apply GST to selected offer types</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {((brands.find(b => b.value === importBrand?.value)?.offerTypes || [])
                          .filter(ot => ot.active !== false && ot.key !== 'flat')
                        ).map(ot => (
                          <label key={ot.key} className={`inline-flex items-center gap-2 text-sm ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                            <input
                              type="checkbox"
                              checked={gstOfferTypes.includes(ot.key)}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setGstOfferTypes(prev => checked ? [...prev, ot.key] : prev.filter(k => k !== ot.key));
                              }}
                              className={`h-4 w-4 text-blue-600 ${darkMode ? 'border-gray-600' : 'border-gray-300'} rounded focus:ring-blue-500`}
                            />
                            {ot.label || ot.key.toUpperCase()}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* File Upload */}
                  <div>
                    <label className={`block text-sm font-bold mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                      Excel File Upload <span className="text-red-500">*</span>
                    </label>
                    <div
                      className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                        dragActive
                          ? (darkMode ? 'border-blue-500 bg-blue-900/20' : 'border-blue-500 bg-blue-50')
                          : (darkMode ? 'border-gray-600 hover:border-blue-500' : 'border-gray-300 hover:border-blue-400')
                      }`}
                      onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                      onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
                      onDrop={(e) => {
                        e.preventDefault();
                        setDragActive(false);
                        const file = e.dataTransfer.files && e.dataTransfer.files[0];
                        if (file) handleImportFile(file);
                      }}
                    >
                      <input
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={(e) => {
                          handleImportFile(e.target.files[0]);
                          e.target.value = '';
                        }}
                        className="hidden"
                        id="employee-file-upload"
                      />
                      <div className="space-y-3">
                        <svg className={`mx-auto w-10 h-10 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        <div>
                          <label
                            htmlFor="employee-file-upload"
                            className={`${darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'} font-semibold cursor-pointer`}
                          >
                            {dragActive ? 'Drop file here' : 'Click to upload'}
                          </label>
                          <span className={`${darkMode ? 'text-gray-400' : 'text-gray-500'}`}> or drag and drop</span>
                        </div>
                        <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>Excel files only (.xlsx, .xls)</p>
                      </div>
                      {importFile && (
                        <div className={`mt-4 p-3 rounded-lg ${darkMode ? 'bg-green-900/20 border border-green-700' : 'bg-green-50 border border-green-200'}`}>
                          <div className="flex items-center justify-center">
                            <svg className={`w-5 h-5 ${darkMode ? 'text-green-400' : 'text-green-600'} mr-2`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className={`font-medium ${darkMode ? 'text-green-300' : 'text-green-700'}`}>{importFile.name}</span>
                          </div>
                          <div className="mt-3 flex gap-2 justify-center">
                            <button
                              type="button"
                              onClick={previewExcelFile}
                              disabled={!importBrand || importing}
                              className={`${darkMode ? 'bg-blue-700 hover:bg-blue-600' : 'bg-blue-600 hover:bg-blue-700'} text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm`}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                              </svg>
                              Check Excel
                            </button>
                          </div>
                          {!importBrand && (
                            <p className="text-xs text-yellow-600 mt-2 text-center">Select a brand to enable Excel validation</p>
                          )}
                          {previewChecked && (
                            <p className="text-xs text-green-600 mt-2 text-center">✓ Excel validated - Ready for import</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <label className={`flex items-center gap-2 mb-3 text-sm cursor-pointer ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <input
                      type="checkbox"
                      checked={strictImport}
                      onChange={(e) => setStrictImport(e.target.checked)}
                      className="h-4 w-4"
                    />
                    Strict mode (reject the whole file if any row has an error)
                  </label>
                  <div className="flex gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <button
                      onClick={handleImportSubmit}
                      disabled={!importBrand || !importCategory || !importFile || importing || !previewChecked}
                      className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 px-6 rounded-lg hover:from-blue-700 hover:to-blue-800 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-3 font-semibold transition-all duration-200"
                    >
                      {importing ? (
                        <span className="flex items-center justify-center gap-2">
                          <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          Processing Import...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                          </svg>
                          Start Import
                        </span>
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setShowImport(false);
                        setImportBrand(null);
                        setImportFile(null);
                        setImportResult(null);
                        setPreviewChecked(false);
                        setPreviewData(null);
                        setShowPreview(false);
                        setImportCategory(null);
                        setImportSeries({ value: 'RS', label: 'Regular Stock (RS)' });
                        setApplyGstToOffers(false);
                        setGstOfferTypes([]);
                      }}
                      className={`px-6 py-3 border-2 rounded-lg font-semibold transition-colors ${
                        darkMode ? 'border-gray-600 hover:bg-gray-700 text-gray-200' : 'border-gray-300 hover:bg-gray-50 text-gray-700'
                      }`}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Import Results */}
                  <div className={`rounded-xl p-6 ${
                    importResult.success
                      ? (darkMode ? 'bg-gradient-to-r from-green-900/30 to-emerald-900/30 border border-green-700' : 'bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200')
                      : (darkMode ? 'bg-gradient-to-r from-red-900/30 to-pink-900/30 border border-red-700' : 'bg-gradient-to-r from-red-50 to-pink-50 border border-red-200')
                  }`}>
                    <div className="flex items-center mb-4">
                      {importResult.success ? (
                        <svg className={`w-8 h-8 ${darkMode ? 'text-green-400' : 'text-green-600'} mr-3`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      ) : (
                        <svg className={`w-8 h-8 ${darkMode ? 'text-red-400' : 'text-red-600'} mr-3`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                      )}
                      <h3 className={`font-bold text-lg ${darkMode ? 'text-green-300' : 'text-green-800'}`}>
                        {importResult.success ? 'Import Completed Successfully' : 'Import Failed'}
                      </h3>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className={`rounded-lg p-4 border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-green-100'}`}>
                        <div className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{importResult.createdCount || 0}</div>
                        <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>New Items Created</div>
                      </div>
                      <div className={`rounded-lg p-4 border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-green-100'}`}>
                        <div className={`text-2xl font-bold ${importResult.errorCount > 0 ? 'text-red-600' : 'text-green-600'}`}>{importResult.errorCount || 0}</div>
                        <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Failed Records</div>
                      </div>
                      {importResult.subItemUpdatedCount > 0 && (
                        <div className={`rounded-lg p-4 border col-span-2 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-green-100'}`}>
                          <div className={`text-2xl font-bold ${darkMode ? 'text-amber-300' : 'text-amber-700'}`}>{importResult.subItemUpdatedCount}</div>
                          <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Items with Sub-items Linked</div>
                        </div>
                      )}
                      {importResult.imageAttachedCount > 0 && (
                        <div className={`rounded-lg p-4 border col-span-2 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-green-100'}`}>
                          <div className={`text-2xl font-bold ${darkMode ? 'text-emerald-300' : 'text-emerald-700'}`}>{importResult.imageAttachedCount}</div>
                          <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Images Attached</div>
                        </div>
                      )}
                      {importResult.variantUpdatedCount > 0 && (
                        <div className={`rounded-lg p-4 border col-span-2 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-green-100'}`}>
                          <div className={`text-2xl font-bold ${darkMode ? 'text-purple-300' : 'text-purple-700'}`}>{importResult.variantUpdatedCount}</div>
                          <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Items with Variants</div>
                        </div>
                      )}
                    </div>
                  </div>

                  {importResult.errors && importResult.errors.length > 0 && (
                    <div className={`rounded-xl p-6 ${darkMode ? 'bg-gradient-to-r from-red-900/30 to-pink-900/30 border border-red-700' : 'bg-gradient-to-r from-red-50 to-pink-50 border border-red-200'}`}>
                      <div className="flex items-center mb-4">
                        <svg className={`w-5 h-5 ${darkMode ? 'text-red-400' : 'text-red-600'} mr-3`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                        <h4 className={`font-bold ${darkMode ? 'text-red-300' : 'text-red-800'}`}>Import Errors ({importResult.errors.length})</h4>
                        <button
                          onClick={() => {
                            const rows = [['Row', 'SKU', 'Message'], ...(importResult.errors || []).map(e => [
                              e && e.row !== undefined ? e.row : '',
                              e && e.skuCode ? e.skuCode : '',
                              e && e.message ? e.message : String(e)
                            ])];
                            const csv = rows.map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
                            const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
                            const a = document.createElement('a');
                            a.href = url; a.download = 'import-errors.csv'; a.click();
                            URL.revokeObjectURL(url);
                          }}
                          className={`ml-auto text-xs px-3 py-1 rounded border ${darkMode ? 'border-red-500 text-red-300 hover:bg-red-900/30' : 'border-red-300 text-red-700 hover:bg-red-100'}`}
                        >
                          Download errors (CSV)
                        </button>
                      </div>
                      <div className={`rounded-lg p-4 max-h-48 overflow-y-auto border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-red-100'}`}>
                        {importResult.errors.slice(0, 10).map((e, i) => (
                          <div key={i} className={`text-sm mb-2 p-2 rounded border-l-4 ${darkMode ? 'text-red-400 bg-red-900/20 border-red-600' : 'text-red-700 bg-red-50 border-red-400'}`}>
                            Row {e.row}: {e.message}
                          </div>
                        ))}
                        {importResult.errors.length > 10 && (
                          <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>... and {importResult.errors.length - 10} more errors</p>
                        )}
                      </div>
                    </div>
                  )}

                  {importResult.warnings && importResult.warnings.length > 0 && (
                    <div className={`rounded-xl p-6 ${darkMode ? 'bg-amber-900/20 border border-amber-700' : 'bg-amber-50 border border-amber-200'}`}>
                      <h4 className={`font-bold mb-3 ${darkMode ? 'text-amber-300' : 'text-amber-800'}`}>Warnings ({importResult.warnings.length})</h4>
                      <div className={`rounded-lg p-4 max-h-48 overflow-y-auto border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-amber-100'}`}>
                        {importResult.warnings.slice(0, 10).map((w, i) => (
                          <div key={i} className={`text-sm mb-2 ${darkMode ? 'text-amber-300' : 'text-amber-800'}`}>
                            {w && w.row !== undefined ? `Row ${w.row}: ` : ''}{w && w.message ? w.message : String(w)}
                          </div>
                        ))}
                        {importResult.warnings.length > 10 && (
                          <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>... and {importResult.warnings.length - 10} more warnings</p>
                        )}
                      </div>
                    </div>
                  )}

                  <button
                    onClick={() => {
                      setShowImport(false);
                      setImportBrand(null);
                      setImportFile(null);
                      setImportResult(null);
                      setPreviewChecked(false);
                      setPreviewData(null);
                      setShowPreview(false);
                      setImportCategory(null);
                      setImportSeries({ value: 'RS', label: 'Regular Stock (RS)' });
                      setApplyGstToOffers(false);
                      setGstOfferTypes([]);
                    }}
                    className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 px-6 rounded-lg hover:from-blue-700 hover:to-blue-800 font-semibold transition-all duration-200"
                  >
                    Close & Refresh
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Excel Preview Modal */}
      {showPreview && previewData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className={`w-full max-w-3xl bg-white dark:bg-gray-800 rounded-xl shadow-2xl overflow-hidden ${
            darkMode ? 'border border-gray-700' : ''
          }`}>
            <div className={`flex items-center justify-between p-4 border-b ${
              darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
            }`}>
              <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Excel Preview - {previewData.totalRows} rows found
              </h3>
              <button
                onClick={() => setShowPreview(false)}
                className={`p-2 rounded-lg transition-colors ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4 max-h-[70vh] overflow-y-auto">
              {/* Validation Results */}
              <div className="mb-4 space-y-2">
                {previewData.drawingNotice && (
                  <div className="p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded text-sm">
                    <span className="font-medium text-yellow-700 dark:text-yellow-300">Image drawings: </span>
                    <span className="text-yellow-700 dark:text-yellow-300">{previewData.drawingNotice}</span>
                  </div>
                )}
                {previewData.missingHeaders.length > 0 && (
                  <div className="p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-sm">
                    <span className="font-medium text-red-700 dark:text-red-300">Missing Required Columns: </span>
                    <span className="text-red-600 dark:text-red-400">{previewData.missingHeaders.join(', ')}</span>
                  </div>
                )}
                {previewData.missingAdditionalHeaders.length > 0 && (
                  <div className="p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded text-sm">
                    <span className="font-medium text-yellow-700 dark:text-yellow-300">Missing Brand Additional Fields: </span>
                    <span className="text-yellow-600 dark:text-yellow-400">{previewData.missingAdditionalHeaders.join(', ')}</span>
                  </div>
                )}
                {previewData.extraHeaders.length > 0 && (
                  <div className="p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded text-sm">
                    <span className="font-medium text-blue-700 dark:text-blue-300">Extra Columns (will be ignored): </span>
                    <span className="text-blue-600 dark:text-blue-400">{previewData.extraHeaders.join(', ')}</span>
                  </div>
                )}
                {previewData.missingHeaders.length === 0 && (
                  <div className="p-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded text-sm">
                    <span className="font-medium text-green-700 dark:text-green-300">✓ All required columns present</span>
                  </div>
                )}
                <div className="p-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded text-sm">
                  <span className="font-medium">Columns Found: </span>
                  <span className="text-gray-600 dark:text-gray-400">{previewData.headers.join(', ')}</span>
                </div>
                {previewData.rowIssues && previewData.rowIssues.length > 0 && (
                  <div className="p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-sm">
                    <div className="font-medium text-red-700 dark:text-red-300 mb-1">
                      Row issues found ({previewData.rowIssues.length}) — fix these before importing:
                    </div>
                    <div className="max-h-40 overflow-y-auto space-y-0.5">
                      {previewData.rowIssues.slice(0, 30).map((issue, i) => (
                        <div key={i} className="text-xs text-red-600 dark:text-red-400">
                          Row {issue.rowNum}: {issue.message}
                        </div>
                      ))}
                      {previewData.rowIssues.length > 30 && (
                        <div className="text-xs text-red-500">…and {previewData.rowIssues.length - 30} more</div>
                      )}
                    </div>
                  </div>
                )}
                {previewData.rowIssues && previewData.rowIssues.length === 0 && (
                  <div className="p-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded text-sm">
                    <span className="font-medium text-green-700 dark:text-green-300">✓ No row issues detected</span>
                  </div>
                )}
              </div>

              {/* Preview view toggle */}
              <div className="flex items-center justify-between mt-3 mb-1">
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {previewViewMode === 'flat'
                    ? `Showing first ${previewData.previewRows.length} of ${previewData.totalRows} rows`
                    : `Grouped structure (first ${(previewData.groups || []).length} items)`}
                </p>
                <div className={`inline-flex rounded-lg border overflow-hidden ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                  <button type="button" onClick={() => setPreviewViewMode('flat')} className={`px-3 py-1 text-xs font-semibold ${previewViewMode === 'flat' ? 'bg-blue-600 text-white' : (darkMode ? 'bg-gray-700 text-gray-200' : 'bg-white text-gray-700')}`}>Flat</button>
                  <button type="button" onClick={() => setPreviewViewMode('grouped')} className={`px-3 py-1 text-xs font-semibold ${previewViewMode === 'grouped' ? 'bg-blue-600 text-white' : (darkMode ? 'bg-gray-700 text-gray-200' : 'bg-white text-gray-700')}`}>Grouped</button>
                </div>
              </div>

              {previewViewMode === 'grouped' ? (
                <div className="overflow-x-auto max-h-[50vh] overflow-y-auto">
                  <div className="space-y-1">
                    {(previewData.groups || []).map((g) => (
                      <div key={g.rowNum}>
                        <div className={`px-2 py-1 rounded ${darkMode ? 'bg-gray-700 text-gray-100' : 'bg-gray-100 text-gray-900'}`}>
                          <span className="font-mono text-xs opacity-60 mr-2">Row {g.rowNum}</span>
                          <span className="font-semibold">{g.data['SKU Code'] || '(no SKU)'}</span>
                          <span className="ml-2">{g.data['SKU Description'] || ''}</span>
                          <span className="ml-2 opacity-70">₹{g.data['MRP'] ?? ''}</span>
                        </div>
                        {(g.children || []).map((c) => (
                          <div key={c.rowNum} className={`ml-6 px-2 py-0.5 text-xs flex flex-wrap gap-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            <span className={`text-[10px] font-bold uppercase ${c.kind === 'variant' ? (darkMode ? 'text-violet-300' : 'text-violet-700') : (darkMode ? 'text-amber-300' : 'text-amber-700')}`}>{c.kind}</span>
                            <span className="font-mono opacity-60">Row {c.rowNum}</span>
                            <span>{c.data['SKU Code'] || ''}</span>
                            <span>{c.kind === 'variant' ? (c.data['Variant'] || '') : (c.data['SKU Description'] || '')}</span>
                            <span className="opacity-70">₹{c.data['MRP'] ?? ''}</span>
                            {c.kind === 'variant' && c.data['Preferred Sub-item'] ? <span className="opacity-70">prefers: {String(c.data['Preferred Sub-item'])}</span> : null}
                            {c.kind === 'sub' && c.data['Sub Qty'] ? <span className="opacity-70">× {c.data['Sub Qty']}</span> : null}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-xs">
                    <thead>
                      <tr className={`${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                        <th className={`px-2 py-1 text-left font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Row</th>
                        {previewData.headers.slice(0, 8).map((h, i) => (
                          <th key={i} className={`px-2 py-1 text-left font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{h}</th>
                        ))}
                        {previewData.headers.length > 8 && <th className={`px-2 py-1 text-left font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>...{previewData.headers.length - 8} more</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.previewRows.map((row) => (
                        <tr key={row.rowNum} className={`border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                          <td className={`px-2 py-1 font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{row.rowNum}</td>
                          {previewData.headers.slice(0, 8).map((h, i) => (
                            <td key={i} className={`px-2 py-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                              {row.data[h] !== undefined && row.data[h] !== null ? String(row.data[h]).substring(0, 20) : ''}
                              {String(row.data[h] || '').length > 20 ? '...' : ''}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className={`flex justify-end p-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <button
                onClick={() => setShowPreview(false)}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors"
              >
                Close Preview
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Premium QR Scanner Modal */}
      {showQRScanner && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto" style={{ animation: 'fadeIn 0.3s ease-out' }}>
          <div className={`rounded-3xl shadow-2xl w-full max-w-lg max-h-[95vh] overflow-y-auto my-auto transform transition-all ${
            darkMode
              ? 'bg-gradient-to-br from-gray-900 to-gray-800 border-2 border-indigo-500/30 shadow-indigo-500/20'
              : 'bg-gradient-to-br from-white to-indigo-50 border-2 border-indigo-200/50 shadow-indigo-500/20'
          }`} style={{ animation: 'slideUp 0.4s ease-out' }}>
            {/* Header */}
            <div className={`px-6 py-4 border-b-2 flex justify-between items-center ${
              darkMode
                ? 'border-gray-700/50 bg-gradient-to-r from-indigo-900/20 to-purple-900/20'
                : 'border-indigo-200/50 bg-gradient-to-r from-indigo-50 to-purple-50'
            }`}>
              <h3 className={`text-xl font-black flex items-center gap-2 ${
                darkMode
                  ? 'text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400'
                  : 'text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600'
              }`}>
                <div className={`p-2 rounded-xl shadow-lg transform hover:scale-110 transition-all ${
                  darkMode
                    ? 'bg-gradient-to-br from-indigo-600 to-purple-600 shadow-indigo-500/50'
                    : 'bg-gradient-to-br from-indigo-500 to-purple-500 shadow-purple-500/30'
                }`}>
                  <FaCamera className="text-white text-lg" />
                </div>
                <span style={{ animation: 'float 3s ease-in-out infinite' }}>Scan QR Code</span>
              </h3>
              <button
                onClick={stopQRScanner}
                className={`p-2 rounded-xl transition-all duration-300 transform hover:scale-110 hover:rotate-90 active:scale-95 ${
                  darkMode
                    ? 'text-red-400 hover:bg-red-900/30 hover:shadow-red-500/50 shadow-lg'
                    : 'text-red-600 hover:bg-red-100 hover:shadow-red-500/30 shadow-md'
                }`}
              >
                <FaTimes size={20} />
              </button>
            </div>

            {/* Camera Selection */}
            <div className={`px-6 py-3 border-b-2 ${
              darkMode
                ? 'border-gray-700/50 bg-gray-800/50'
                : 'border-indigo-100 bg-indigo-50/30'
            }`}>
              <label className={`block text-xs font-black uppercase tracking-wider mb-2 ${
                darkMode ? 'text-indigo-400' : 'text-indigo-700'
              }`}>
                📹 Select Camera:
              </label>
              <select
                value={selectedCameraId}
                    onChange={(e) => {
                  const newCamera = e.target.value;
                  setSelectedCameraId(newCamera);
                }}
                className={`w-full px-3 py-2 text-sm font-bold rounded-xl border-2 transition-all focus:outline-none focus:ring-4 shadow-md ${
                  darkMode
                    ? 'bg-gray-700 border-gray-600 text-white focus:ring-indigo-500/30 focus:border-indigo-500 hover:border-indigo-600'
                    : 'bg-white border-indigo-300 text-gray-900 focus:ring-indigo-500/30 focus:border-indigo-500 hover:border-indigo-400'
                }`}
              >
                {availableCameras.length === 0 && (
                  <option value="">No cameras detected</option>
                )}
                {availableCameras.map((camera) => (
                  <option key={camera.id} value={camera.id}>
                    {camera.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="p-6">
              {/* Scanner Frame */}
              <div className={`relative rounded-2xl overflow-hidden shadow-2xl border-4 w-full h-[380px] ${
                darkMode
                  ? 'border-indigo-500/50 shadow-indigo-500/30 bg-black'
                  : 'border-indigo-300/50 shadow-indigo-500/20 bg-black'
              }`}>
                <div className="w-full h-full">
                  <QRScanner
                    key={selectedCameraId || 'default'}
                    onScan={handleQRScan}
                    cameraId={selectedCameraId}
                    onCameraError={handleQRError}
                    onLoading={handleCameraLoad}
                  />
                </div>

                {/* Scanner Overlay Frame */}
                <div className="absolute inset-0 pointer-events-none">
                  <div className="absolute inset-0 border-4 border-transparent" style={{
                    background: 'linear-gradient(to right, transparent 40%, rgba(99, 102, 241, 0.1) 50%, transparent 60%)',
                    animation: 'scanLine 2s linear infinite'
                  }}></div>

                  {/* Corner Markers */}
                  <div className={`absolute top-3 left-3 w-10 h-10 border-t-4 border-l-4 rounded-tl-xl ${
                    darkMode ? 'border-indigo-500' : 'border-indigo-500'
                  }`}></div>
                  <div className={`absolute top-3 right-3 w-10 h-10 border-t-4 border-r-4 rounded-tr-xl ${
                    darkMode ? 'border-indigo-500' : 'border-indigo-500'
                  }`}></div>
                  <div className={`absolute bottom-3 left-3 w-10 h-10 border-b-4 border-l-4 rounded-bl-xl ${
                    darkMode ? 'border-indigo-500' : 'border-indigo-500'
                  }`}></div>
                  <div className={`absolute bottom-3 right-3 w-10 h-10 border-b-4 border-r-4 rounded-br-xl ${
                    darkMode ? 'border-indigo-500' : 'border-indigo-500'
                  }`}></div>
                </div>

                {qrScannerLoading && (
                  <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex flex-col items-center justify-center">
                    <div className={`animate-spin rounded-full h-12 w-12 border-b-4 mb-3 ${
                      darkMode ? 'border-indigo-500' : 'border-indigo-500'
                    }`}></div>
                    <p className="text-white text-center font-bold text-base">
                      Initializing Camera...
                    </p>
                  </div>
                )}
              </div>

              {/* Instructions */}
              <div className={`mt-4 p-3 rounded-2xl border-2 text-center ${
                darkMode
                  ? 'bg-gray-800/50 border-gray-700 text-gray-300'
                  : 'bg-gradient-to-br from-indigo-50 to-pink-50 border-indigo-200 text-gray-700'
              }`}>
                <p className="text-xs font-bold">
                  📱 Position the QR code within the frame to scan automatically
                </p>
              </div>

              {/* Permission Notice */}
              <div className={`mt-3 p-3 rounded-2xl border-2 shadow-lg ${
                darkMode
                  ? 'bg-indigo-900/30 border-indigo-700/50 text-indigo-300'
                  : 'bg-indigo-50 border-indigo-200 text-indigo-700'
              }`}>
                <p className="text-xs font-bold text-center flex items-center justify-center gap-2">
                  <span className="text-base">💡</span>
                  <span>Make sure to allow camera permissions when prompted by your browser</span>
                </p>
              </div>

              {/* Action Buttons */}
              <div className="mt-4 flex justify-center gap-3">
                <button
                  onClick={stopQRScanner}
                  className={`px-6 py-2.5 rounded-xl font-black shadow-lg transition-all duration-300 transform hover:scale-105 active:scale-95 text-sm ${
                    darkMode
                      ? 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white shadow-red-500/50'
                      : 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-400 hover:to-red-500 text-white shadow-red-500/30'
                  }`}
                >
                  🛑 Stop Scanning
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes scanLine {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(100%); }
        }
      `}</style>

      {/* QR Code Modal */}
      {showQRModal && qrItem && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className={`rounded-2xl max-w-md w-full max-h-[90vh] my-4 shadow-2xl flex flex-col ${
            darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white'
          }`}>
            {/* Fixed Header */}
            <div className={`flex items-center justify-between p-4 sm:p-5 border-b flex-shrink-0 ${
              darkMode ? 'border-gray-700' : 'border-gray-200'
            }`}>
              <h3 className={`text-base sm:text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                QR Code - {qrItem.skuCode}
              </h3>
              <button
                onClick={() => { setShowQRModal(false); setQrCodeDataUrl(null); }}
                className={`p-2 rounded-xl transition-colors flex-shrink-0 ${
                  darkMode ? 'text-gray-400 hover:text-white hover:bg-gray-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
                title="Close"
              >
                <FaTimes className="w-5 h-5 sm:w-5 sm:h-5" />
              </button>
            </div>

            {/* Hidden QR SVG for PDF generation */}
            <div style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
              <QRCodeSVG id="item-qr-code-svg" value={buildQrValue(qrItem)} size={200} bgColor="#ffffff" fgColor="#000000" />
            </div>

            {/* Scrollable Content */}
            <div className="overflow-y-auto p-4 sm:p-5 flex-1">
              <div className={`p-4 sm:p-5 rounded-xl border ${
                darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
              }`}>
                <h4 className={`text-sm sm:text-base font-semibold mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                  Item QR Code
                </h4>
                <div className="flex flex-col items-center space-y-3">
                  <div className="bg-white p-3 sm:p-4 rounded-lg">
                    {qrCodeDataUrl ? (
                      <img src={qrCodeDataUrl} alt="QR Code" className="w-48 h-48 sm:w-64 sm:h-64" />
                    ) : (
                      <div className="w-48 h-48 sm:w-64 sm:h-64 flex items-center justify-center">
                        <svg className="animate-spin h-8 w-8 text-gray-400" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                      </div>
                    )}
                  </div>
                  <p className={`text-xs sm:text-sm text-center break-words px-2 font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {qrItem.skuDescription}
                  </p>
                  <p className={`text-xs sm:text-sm text-center break-all px-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    ID: {qrItem._id}
                  </p>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {qrCodeDataUrl && (
                      <button
                        onClick={handleDownloadQR}
                        className="px-3 sm:px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-2 transition-colors text-sm"
                      >
                        <FaDownload className="w-4 h-4" />
                        <span>PNG</span>
                      </button>
                    )}
                    {qrCodeDataUrl && (
                      <button
                        onClick={() => { setTimeout(() => downloadQRPdf(false), 100); }}
                        className="px-3 sm:px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg flex items-center gap-2 transition-colors text-sm"
                      >
                        <FaFilePdf className="w-4 h-4" />
                        <span>PDF</span>
                      </button>
                    )}
                    {qrCodeDataUrl && (
                      <button
                        onClick={() => { setTimeout(() => downloadQRPdf(true), 100); }}
                        className="px-3 sm:px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 transition-colors text-sm"
                      >
                        <FaFilePdf className="w-4 h-4" />
                        <span>PDF + Name</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* QR Name Info */}
              {qrName ? (
                <p className={`text-xs text-center mt-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  PDF + Name will print with the company Q.R. name: <span className="font-semibold">{qrName}</span>
                </p>
              ) : (
                <p className={`text-xs text-center mt-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Using default name "DOMUS" for PDF with name (QR Name not configured for company)
                </p>
              )}
            </div>

            {/* Footer */}
            <div className={`flex justify-end gap-2 p-4 border-t flex-shrink-0 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <button
                onClick={() => { setShowQRModal(false); setQrCodeDataUrl(null); }}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white rounded-lg font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      </div>
    </div>
  );
};

export default ItemManagement;
